/**
 * ########################################################################
 * # Drumland Custom Kit Builder
 * # Designed and developed by ARMM Creative Inc. for Drumland Canada
 * # https://www.armmcreative.com/
 * # info@armmcreative.com
 * # Copyright Â© 2026 ARMM Creative Inc. All rights reserved.
 * ########################################################################
 */

(() => {
  console.info(
    "%cDRUMLAND CUSTOM KIT BUILDER\nDesigned and developed by ARMM Creative Inc. for Drumland Canada\nhttps://www.armmcreative.com/ Â· info@armmcreative.com\nCopyright Â© 2026 ARMM Creative Inc. All rights reserved.",
    "color:#c81e2b;border:2px solid #c81e2b;padding:8px 12px;font-weight:700;line-height:1.5;"
  );

  const REWARD_POINTS_EARNED_PER_DOLLAR = 5;
  const REWARD_POINTS_PER_REDEMPTION_DOLLAR = 100;
  const AFFIRM_MONTHS_SHORT = 12;
  const AFFIRM_MONTHS_LONG = 24;
  const AFFIRM_LONG_APR = 0.15;
  const CART_ADD_ENDPOINT = "/cart/add.js";
  const QUOTE_RETURN_PARAM = "ddkb_quote";
  const QUOTE_CONFIRMATION_STORAGE_PREFIX = "ddkb-quote-confirmation:";
  const MOBILE_LAYOUT_QUERY = "(max-width: 900px)";

  class DreamDrumKitBuilder {
    constructor(root) {
      this.root = root;
      this.config = this.readConfig();
      this.catalog = [];
      this.catalogMap = new Map();
      this.catalogLoaded = false;
      this.catalogError = false;
      this.catalogErrorMessage = "";
      this.validationWarnings = new Set();
      this.elements = {};
      this.confirmationSnapshot = null;
      this.state = this.readInitialState();

      this.render();
      this.mountShopifyContactForm();
      this.cacheElements();
      this.bindEvents();
      this.updateStickyOffset();
      this.update();
      this.loadCatalog();
      // After a real Shopify contact POST, return_to lands back here with the rich confirmation.
      this.maybeRestoreQuoteConfirmation();
    }

    readConfig() {
      const parsed = safeJson(this.root.querySelector("[data-ddkb-config]")?.textContent, {});
      const primaryMaterialKey = String(parsed.primaryMaterialKey || parsed.primary_material_key || "wood").trim() || "wood";

      return {
        productTitle: "DW Performance Series Custom Build",
        shopName: "Drumland",
        heroImage: "",
        heroImageAlt: "Custom drum kit",
        eyebrow: "DW Performance Series / Custom Shop Quote",
        heroHeadline: "Build the kit around the player, not the product page.",
        heroPoint1: "Built to order",
        heroPoint2: "Expert consultation included",
        heroPoint3: "Phone confirmation before order",
        heroCaption: "__empty__",
        defaultSeriesKey: "dw-performance",
        defaultWoodKey: "maple",
        catalogJsonUrl: "",
        requestButtonLabel: "Submit Build Request",
        shareButtonLabel: "Share build",
        quoteDisclaimer: "This is an estimate for planning only. Final pricing, availability, taxes, shipping, and payment terms will be confirmed before any order is placed. Quote is valid for 30 days.",
        rewardsUrl: "/pages/drumland-rewards",
        rewardsLabel: "Drumland Rewards",
        rewardsDescription: "Earn 5 points per $1 spent. Points are redeemable on future purchases and do not reduce the price of this build. Redemption value is estimated at 100 points = $1.",
        rewardsLinkLabel: "View rewards program",
        financingUrl: "/pages/financing",
        financingLabel: "0% and interest free",
        financingDescription: "Planning estimate only. Live payment options depend on checkout eligibility.",
        financingLinkLabel: "View financing details",
        supportEmail: "",
        supportPhone: "",
        savingsStepLabel: "Rewards and Financing",
        reviewStepLabel: "Review",
        showRewards: true,
        showFinancing: true,
        enableMotion: true,
        ...parsed,
        primaryMaterialKey,
        categoryBlocks: this.normalizeCategoryBlocks(parsed.categoryBlocks || [], primaryMaterialKey),
        optionBlocks: this.normalizeOptionBlocks(parsed.optionBlocks || []),
        imageCombos: this.normalizeImageCombos(parsed.imageCombos || []),
        finishBlocks: this.normalizeFinishBlocks(parsed.finishBlocks || []),
        shellCategoryBlocks: this.normalizeShellCategoryBlocks(parsed.shellCategoryBlocks || []),
        shellImageBlocks: this.normalizeShellImageBlocks(parsed.shellImageBlocks || []),
        addOns: normalizeAddOns(parsed.addOns || [])
      };
    }

    normalizeFinishBlocks(blocks) {
      return blocks.map((block, index) => ({
        id: String(block.id || `finish-${index}`),
        name: block.name || block.finishName || "Finish",
        key: String(block.key || block.finishKey || slugify(block.name)).trim(),
        familyLabel: block.familyLabel || "",
        description: block.description || "",
        swatchImage: block.swatchImage || "",
        frontImage: block.frontImage || "",
        backImage: block.backImage || "",
        defaultFinish: !!block.defaultFinish,
        sortOrder: toNumber(block.sortOrder || index)
      })).filter(block => block.key).sort(sortBySortOrderThenName);
    }

    normalizeShellCategoryBlocks(blocks) {
      return blocks.map((block, index) => ({
        id: String(block.id || `shell-category-${index}`),
        label: block.label || "Shells",
        drumTypeKey: String(block.drumTypeKey || block.drumType || slugify(block.label)).trim(),
        description: block.description || "",
        sortOrder: toNumber(block.sortOrder || index),
        defaultOpen: block.defaultOpen !== false,
        allowQuantity: block.allowQuantity !== false
      })).filter(block => block.drumTypeKey).sort(sortBySortOrderThenLabel);
    }

    normalizeShellImageBlocks(blocks) {
      const byImageKey = new Map();
      const byCatalogId = new Map();
      const list = [];
      blocks.forEach(block => {
        const imageKey = String(block.imageKey || "").trim();
        const catalogId = String(block.catalogId || "").trim();
        const override = {
          catalogId,
          imageKey,
          image: block.image || "",
          altText: block.altText || "",
          displayLabel: block.displayLabel || "",
          description: block.description || "",
          priceOverride: optionalNumber(block.priceOverride),
          sortOrder: optionalNumber(block.sortOrder)
        };
        if (!catalogId && !imageKey) return;
        if (catalogId) byCatalogId.set(catalogId, override);
        if (imageKey) byImageKey.set(imageKey, override);
        list.push(override);
      });
      return { byImageKey, byCatalogId, list };
    }

    normalizeImageCombos(blocks) {
      return blocks.map((block, index) => {
        const images = [block.image || block.comboImage || "", block.image2 || block.comboImage2 || ""].filter(Boolean);
        return {
          id: String(block.id || `image-combo-${index}`),
          optionKey1: String(block.optionKey1 || block.option_key_1 || "").trim(),
          optionKey2: String(block.optionKey2 || block.option_key_2 || "").trim(),
          categoryKey1: String(block.categoryKey1 || block.category_key_1 || "").trim(),
          categoryKey2: String(block.categoryKey2 || block.category_key_2 || "").trim(),
          label: block.label || block.comboLabel || block.combo_label || "",
          images,
          sortOrder: toNumber(block.sortOrder ?? block.sort_order ?? index)
        };
      })
        .filter(block => block.optionKey1 && block.optionKey2 && block.images.length)
        .sort((a, b) => a.sortOrder - b.sortOrder || naturalCompare(a.label, b.label));
    }

    normalizeCategoryBlocks(blocks, primaryMaterialKey = "wood") {
      return blocks.map((block, index) => {
        const orderIndex = toNumber(block.orderIndex ?? index);
        const stepNumber = optionalNumber(block.stepNumber ?? block.step_number);
        const sortOrder = stepNumber ?? ((orderIndex + 1) * 10);
        const dataSource = this.normalizeDataSource(block.displayMode || block.display_mode || block.dataSource || block.data_source);
        const groupField = String(block.groupField || block.group_field || "").trim()
          || (dataSource === "catalog_select" ? "drumTypeLabel" : "");
        const filterField = String(block.filterField || block.filter_field || block.catalogField || block.catalog_field || "").trim();
        const categoryKey = String(block.key || block.categoryKey || slugify(block.label) || `step-${index}`).trim();
        const includeValues = parseDelimitedList(block.includeValues || block.include_values || block.includeGroups || block.include_groups);
        const categoryIdentifiers = [categoryKey, block.label || block.categoryLabel || "", ...includeValues]
          .map(normalizeAddonCategoryKey);
        const isSnareCategory = dataSource === "catalog_select" &&
          categoryIdentifiers.some(value => value === "sn" || value.includes("snare"));
        const configuredSubgroupField = String(block.subgroupField || block.subgroup_field || "").trim();
        const configuredEyebrowField = String(block.eyebrowField || block.eyebrow_field || "").trim();
        const configuredTitleField = String(block.titleField || block.title_field || "").trim();
        const configuredLabelField = String(block.labelField || block.label_field || "").trim();
        const isLegacySnareConfiguration = isSnareCategory && !configuredSubgroupField &&
          !configuredEyebrowField && !configuredTitleField;
        const resolvedGroupField = isLegacySnareConfiguration && (!groupField || groupField === "finishSelectorLabel")
          ? "finishFamilyLabel"
          : groupField;
        const resolvedSubgroupField = configuredSubgroupField ||
          (isLegacySnareConfiguration ? "finishSelectorLabel" : "");
        const resolvedCatalogField = dataSource === "catalog_select"
          ? filterField
          : (filterField || this.inferCatalogFieldFromKey(categoryKey, block.label || block.categoryLabel, dataSource, primaryMaterialKey));

        return {
          id: String(block.id || `category-${index}`),
          key: categoryKey,
          label: isSnareCategory && normalizeAddonCategoryKey(block.label || block.categoryLabel) === "snare sizes"
            ? "Snare Options"
            : (block.label || block.categoryLabel || "Step"),
          help: block.help || block.categoryHelp || "",
          dataSource,
          filterField: resolvedCatalogField,
          catalogField: resolvedCatalogField,
          labelField: configuredLabelField,
          groupField: resolvedGroupField,
          subgroupField: resolvedSubgroupField,
          eyebrowField: configuredEyebrowField ||
            (dataSource === "catalog_select" ? (resolvedSubgroupField || "finishSelectorLabel") : ""),
          titleField: configuredTitleField ||
            (dataSource === "catalog_select" ? (configuredLabelField || resolvedCatalogField) : ""),
          singleOptionLayout: ["automatic", "standard", "double"].includes(block.singleOptionLayout || block.single_option_layout)
            ? (block.singleOptionLayout || block.single_option_layout)
            : "automatic",
          autoSelectSingleOption: !!(block.autoSelectSingleOption ?? block.auto_select_single_option),
          stackOptionsVertically: (block.stackOptionsVertically ?? block.stack_options_vertically) === true,
          showSelectionChangeWarning: block.showSelectionChangeWarning ?? block.show_selection_change_warning ?? true,
          prioritizeActiveSelection: !!(block.prioritizeActiveSelection ?? block.prioritize_active_selection),
          priorityCategoryKey: String(block.priorityCategoryKey || block.priority_category_key || "").trim(),
          priorityRowField: String(block.priorityRowField || block.priority_row_field || "").trim(),
          inheritsPrevious: block.inheritsPrevious ?? block.inherits_previous ?? true,
          rowFilterField: String(block.rowFilterField || block.row_filter_field || block.scopeField || block.scope_field || "").trim(),
          scopeField: String(block.scopeField || block.scope_field || "").trim(),
          excludeFilterValues: parseDelimitedList(block.excludeFilterValues || block.exclude_filter_values),
          includeValues,
          excludeValues: parseDelimitedList(block.excludeValues || block.exclude_values || block.excludeGroups || block.exclude_groups),
          includeGroups: parseDelimitedList(block.includeValues || block.include_values || block.includeGroups || block.include_groups),
          excludeGroups: parseDelimitedList(block.excludeValues || block.exclude_values || block.excludeGroups || block.exclude_groups),
          ignoreUpstreamCategoryKeys: parseDelimitedList(block.ignoreUpstreamCategoryKeys || block.ignore_upstream_category_keys),
          filterOrder: String(block.filterOrder || block.filter_order || "").trim(),
          addonCategory: String(block.addonCategory || block.addon_category || "").trim(),
          multiSelect: dataSource === "catalog_select" ? block.multiSelect !== false : !!block.multiSelect,
          allowQuantity: block.allowQuantity !== false,
          stepNumber: sortOrder,
          orderIndex,
          sortOrder
        };
      })
        .filter(block => block.key)
        .filter(block => block.dataSource !== "savings" && block.dataSource !== "review")
        .sort((a, b) => a.sortOrder - b.sortOrder || a.orderIndex - b.orderIndex);
    }

    normalizeDataSource(value) {
      const source = String(value || "catalog_field").trim().toLowerCase();
      const aliases = {
        field: "catalog_field",
        catalog: "catalog_field",
        picker: "catalog_field",
        filter: "catalog_field",
        filters: "catalog_field",
        select: "catalog_select",
        products: "catalog_select",
        items: "catalog_select",
        "catalog-items": "catalog_select",
        catalog_items: "catalog_select",
        options: "manual",
        manual: "manual",
        addon: "addons",
        addons: "addons",
        savings: "savings",
        review: "review"
      };
      return aliases[source] || source;
    }

    normalizeOptionBlocks(blocks) {
      const list = blocks.map((block, index) => ({
        id: String(block.id || `option-${index}`),
        key: String(block.key || "").trim(),
        categoryKeys: parseDelimitedList(block.categoryKeys || block.category_keys || block.categoryKey || block.category_key),
        categoryKey: parseDelimitedList(block.categoryKeys || block.category_keys || block.categoryKey || block.category_key)[0] || "",
        scope: this.normalizeOptionScope(block.scope || block.optionScope || block.option_scope),
        matchField: String(block.matchField || block.match_field || "").trim(),
        label: block.label || "",
        description: block.description || "",
        thumbnailImage: block.thumbnailImage || "",
        previewImage: block.previewImage || "",
        previewImage2: block.previewImage2 || "",
        priceOverride: optionalNumber(block.priceOverride),
        sortOrder: optionalNumber(block.sortOrder),
        defaultOption: !!block.defaultOption
      })).filter(block => block.key);

      const byScope = new Map();
      list.forEach(block => {
        if (!byScope.has(block.scope)) byScope.set(block.scope, new Map());
        byScope.get(block.scope).set(block.key, block);
      });

      return { list, byScope };
    }

    normalizeOptionScope(value) {
      const scope = String(value || "any").trim().toLowerCase();
      const aliases = {
        material: "wood",
        shell: "wood",
        "shell-material": "wood",
        finish: "finish",
        category: "category",
        "shell-category": "category",
        drumtype: "category",
        catalog: "catalog",
        "catalog-id": "catalog",
        image: "image",
        "image-key": "image",
        any: "any"
      };
      return aliases[scope] || scope;
    }

    optionTargetsCategory(option, categoryKey) {
      if (!option || !categoryKey) return false;
      const keys = Array.isArray(option.categoryKeys) && option.categoryKeys.length
        ? option.categoryKeys
        : (option.categoryKey ? [option.categoryKey] : []);
      return keys.includes(categoryKey);
    }

    async loadCatalog() {
      const items = await fetchJson(this.config.catalogJsonUrl, null);
      if (!items) {
        this.catalogError = true;
        this.catalogErrorMessage = "Builder catalog could not be loaded. Please check the catalog JSON setting.";
        this.warn("catalog file failed to load", this.config.catalogJsonUrl);
        this.update();
        return;
      }

      this.catalog = this.normalizeCatalog(items);
      const configuredSeries = String(this.config.defaultSeriesKey || "").trim();
      const availableSeries = [...new Set(this.catalog.map(row => row.series).filter(Boolean))];
      const seriesCategories = this.getCategories().filter(category =>
        category.dataSource === "catalog_field" && this.catalogFieldForCategory(category) === "series"
      );
      const seriesCategory = seriesCategories[0];
      const firstCatalogCategory = this.getCategories().find(category =>
        ["catalog_field", "catalog_select"].includes(category.dataSource)
      );
      if (!configuredSeries && (!seriesCategory || seriesCategories.length !== 1 || seriesCategory.multiSelect || firstCatalogCategory !== seriesCategory)) {
        this.catalogError = true;
        this.catalogErrorMessage = 'Leave Default series key blank only when one single-select Category step using Filter field "series" is placed before the other catalog steps.';
        this.warn("series category is missing or invalid", { availableSeries });
        this.update();
        return;
      }
      if (configuredSeries && !availableSeries.includes(configuredSeries)) {
        this.catalogError = true;
        this.catalogErrorMessage = `Series "${configuredSeries}" was not found in the selected catalog JSON.`;
        this.warn("configured series does not exist in catalog", { configuredSeries, availableSeries });
        this.update();
        return;
      }

      this.syncActiveSeries();
      this.catalogMap = new Map(this.catalog.map(row => [row.catalogId, row]));
      this.catalogLoaded = true;
      if (!configuredSeries && !this.getCatalogFieldCategoryOptions(seriesCategory).length) {
        this.catalogError = true;
        this.catalogErrorMessage = "No series are available. Check the Series category row filters and excluded filter values.";
      }
      this.validateBlockCatalogCoverage();
      this.ensureValidSelections();
      this.restoreSelectedShellsAfterCatalogLoad();
      this.mountMotion();
      this.update();
    }

    readInitialState() {
      const params = new URLSearchParams(window.location.search);
      const categories = this.getCategories();
      const stepOrder = this.getStepOrder();
      const selections = {};
      const catalogSelections = {};
      const categoryFilters = {};

      categories.forEach(category => {
        const legacyParam = this.legacyParamForCategory(category);
        const rawValue = params.has(category.key)
          ? params.get(category.key)
          : (legacyParam && params.has(legacyParam) ? params.get(legacyParam) : null);

        if (category.dataSource === "catalog_select") {
          const shellSource = rawValue ?? (category.key === "shells" ? params.get("shells") : null);
          catalogSelections[category.key] = this.parseShellParam(shellSource || "");
          return;
        }

        if (category.dataSource === "catalog_field" || category.dataSource === "manual") {
          if (rawValue == null) return;
          selections[category.key] = category.multiSelect
            ? rawValue.split(",").filter(Boolean)
            : rawValue;
        }
      });

      const defaultMaterialCategory = categories.find(category =>
        category.dataSource === "catalog_field" &&
        this.catalogFieldForCategory(category) === this.config.primaryMaterialKey
      );
      if (defaultMaterialCategory && !selections[defaultMaterialCategory.key] && params.get("wood")) {
        selections[defaultMaterialCategory.key] = params.get("wood");
      }

      const finishCategory = categories.find(category =>
        category.dataSource === "catalog_field" &&
        this.catalogFieldForCategory(category) === "finishKey"
      );
      const shellSelectCategory = categories.find(category => category.dataSource === "catalog_select");
      if (params.get("finishGroup") && finishCategory) {
        categoryFilters[finishCategory.key] = params.get("finishGroup");
      }
      if (params.get("shellGroup") && shellSelectCategory) {
        categoryFilters[shellSelectCategory.key] = params.get("shellGroup");
      }
      categories.forEach(category => {
        const filterParam = this.categoryFilterParamName(category);
        if (!filterParam || !params.get(filterParam)) return;
        categoryFilters[category.key] = params.get(filterParam);
      });

      return {
        activeStep: stepOrder.includes(params.get("step")) ? params.get("step") : (stepOrder[0] || ""),
        series: String(this.config.defaultSeriesKey || "").trim(),
        selections,
        catalogSelections,
        categoryFilters: {
          ...categoryFilters
        },
        photoIndex: params.get("kitView") === "back" ? 1 : 0,
        lastDisplayedPhoto: null,
        activeAddonPreview: null,
        addOns: new Set((params.get("addons") || "").split(",").filter(Boolean)),
        activeAddonFilter: "",
        expandedAddonRows: new Set(),
        restoreWarnings: [],
        railCollapsed: window.matchMedia?.(MOBILE_LAYOUT_QUERY).matches || false,
        mobileQuoteExpanded: false,
        mobilePreviewHidden: false,
        quoteNumber: "",
        quoteSubmitted: false,
        quoteSending: false
      };
    }

    legacyParamForCategory(category) {
      const field = this.catalogFieldForCategory(category);
      if (field === this.config.primaryMaterialKey || field === "wood") return "wood";
      if (field === "finishKey") return "finish";
      // Legacy share links only used a single shells= param for the main shell step.
      // Never map snare (or other catalog_select steps) to shells â€” that dumped every shell under Snare Options.
      if (category.dataSource === "catalog_select" && category.key === "shells") return "shells";
      return "";
    }

    inferCatalogFieldFromKey(categoryKey, categoryLabel = "", dataSource = "catalog_field", primaryMaterialKey = "") {
      if (dataSource === "catalog_select") return "";

      const key = String(categoryKey || "").trim().toLowerCase();
      const label = String(categoryLabel || "").trim().toLowerCase();
      const materialKey = primaryMaterialKey || this.config?.primaryMaterialKey || "wood";
      if (key === materialKey || key === "wood" || key.includes("material") || label.includes("material")) return materialKey;
      if (key === "finish" || key.includes("finish") || label.includes("finish")) return "finishKey";
      if (key === "shells" || key === "sizes" || key.includes("size") || label.includes("size")) return "sizeKey";
      return key;
    }

    catalogFieldForCategory(category) {
      if (!category) return "";
      const explicit = String(category.filterField || category.catalogField || "").trim();
      if (explicit) return explicit;
      return this.inferCatalogFieldFromKey(category.key, category.label, category.dataSource);
    }

    categoryInheritsPrevious(category) {
      if (!category) return true;
      return category.inheritsPrevious !== false;
    }

    getResolvedCategorySelection(category) {
      const value = this.getCategorySelection(category.key);
      if (value != null && value !== "" && !(Array.isArray(value) && !value.length)) return value;
      if (category.dataSource !== "catalog_field") return value;

      const field = this.catalogFieldForCategory(category);
      const materialKey = this.config.primaryMaterialKey || "wood";
      if (field === materialKey || field === "wood") {
        const options = this.getCatalogFieldCategoryOptions(category);
        const configuredDefault = options.find(option => option.key === this.config.defaultWoodKey);
        return configuredDefault?.key || options[0]?.key || null;
      }
      if (field === "finishKey" && this.catalogLoaded) {
        const options = this.getCatalogFieldCategoryOptions(category);
        const fallback = options.find(option => option.defaultOption) || options[0];
        return fallback?.key || null;
      }
      return value;
    }

    pruneStaleCategoryFilter(categoryKey, validLabels) {
      const active = this.getCategoryFilter(categoryKey);
      if (!active) return;
      const labels = new Set(validLabels.filter(Boolean));
      if (!labels.has(active)) this.setCategoryFilter(categoryKey, "");
    }

    getCategories() {
      const userSteps = this.config.categoryBlocks || [];
      const userStepCount = userSteps.length;
      const maxSort = userSteps.reduce((max, step) => Math.max(max, step.sortOrder || 0), 0);
      const savingsStep = {
        id: "__system-savings__",
        key: "savings",
        label: this.config.savingsStepLabel || "Rewards and Financing",
        help: "",
        dataSource: "savings",
        stepNumber: userStepCount + 1,
        sortOrder: maxSort + 10,
        orderIndex: 9001,
        isSystemStep: true
      };
      const reviewStep = {
        id: "__system-review__",
        key: "review",
        label: this.config.reviewStepLabel || "Review",
        help: "",
        dataSource: "review",
        stepNumber: userStepCount + 2,
        sortOrder: maxSort + 20,
        orderIndex: 9002,
        isSystemStep: true
      };
      return [...userSteps, savingsStep, reviewStep];
    }

    getStepOrder() {
      return this.getCategories().map(category => category.key);
    }

    getCategory(key) {
      return this.getCategories().find(category => category.key === key) || null;
    }

    getSeriesCategory() {
      return this.getCategories().find(category =>
        category.dataSource === "catalog_field" && this.catalogFieldForCategory(category) === "series"
      ) || null;
    }

    syncActiveSeries() {
      const configuredSeries = String(this.config.defaultSeriesKey || "").trim();
      const category = this.getSeriesCategory();
      const selected = category ? this.getCategorySelection(category.key) : null;
      this.state.series = configuredSeries || (typeof selected === "string" ? selected : "");
    }

    catalogRowsForActiveSeries() {
      if (!this.state.series) return [];
      return this.catalog.filter(row => row.series === this.state.series);
    }

    getCategorySelection(categoryKey) {
      return this.state.selections?.[categoryKey] ?? null;
    }

    getCategoryCatalogSelection(categoryKey) {
      return this.state.catalogSelections?.[categoryKey] || [];
    }

    getCategoryFilter(categoryKey) {
      return this.state.categoryFilters?.[categoryKey] || "";
    }

    setCategoryFilter(categoryKey, value) {
      if (!this.state.categoryFilters) this.state.categoryFilters = {};
      this.state.categoryFilters[categoryKey] = value || "";
    }

    isMobileLayout() {
      return typeof window !== "undefined" && window.matchMedia(MOBILE_LAYOUT_QUERY).matches;
    }

    resolveCategoryFilter(categoryKey, labels = []) {
      const uniqueLabels = [...new Set(labels.filter(Boolean))];
      const active = this.getCategoryFilter(categoryKey);
      if (active) return active;
      // Mobile filter pills have no "All" tab â€” default to the first group.
      if (this.isMobileLayout() && uniqueLabels.length) return uniqueLabels[0];
      return "";
    }

    getOptionsForCategory(categoryKey) {
      const category = this.getCategory(categoryKey);
      if (!category) return [];
      if (category.dataSource === "manual") return this.getManualCategoryOptions(category);
      if (category.dataSource === "catalog_field") return this.getCatalogFieldCategoryOptions(category);
      return [];
    }

    getManualCategoryOptions(category) {
      return this.config.optionBlocks.list
        .filter(option => this.optionTargetsCategory(option, category.key))
        .map((option, index) => this.presentOption({
          key: option.key,
          label: option.label || titleFromKey(option.key),
          description: option.description || "",
          thumbnailImage: option.thumbnailImage || "",
          previewImage: option.previewImage || "",
          previewImage2: option.previewImage2 || "",
          defaultOption: option.defaultOption,
          sortOrder: option.sortOrder ?? index
        }, option))
        .sort((a, b) => (a.sortOrder ?? 100) - (b.sortOrder ?? 100) || naturalCompare(a.label, b.label));
    }

    getCatalogFieldCategoryOptions(category) {
      const field = this.catalogFieldForCategory(category);
      const labelField = category.labelField || (field === "series" ? "seriesLabel" : "");
      const rows = this.catalogRowsBeforeCategory(category.key);
      const map = new Map();
      const excludedOptionValues = new Set(
        (Array.isArray(category.excludeFilterValues) ? category.excludeFilterValues : [])
          .flatMap(value => [String(value || "").trim(), this.normalizeFilterMatchKey(value), normalizeAddonCategoryKey(value)])
          .filter(Boolean)
      );

      rows.forEach(row => {
        const key = String(row[field] || "").trim();
        if (excludedOptionValues.has(key) || excludedOptionValues.has(this.normalizeFilterMatchKey(key)) || excludedOptionValues.has(normalizeAddonCategoryKey(key))) return;
        if (!key || map.has(key)) return;
        const label = labelField ? String(row[labelField] || "").trim() : "";
        map.set(key, {
          key,
          label: label || titleFromKey(key),
          description: "",
          group: category.groupField ? String(row[category.groupField] || "").trim() : "",
          sortOrder: row.sortOrder ?? 100
        });
      });

      return [...map.values()].map(option => {
        const override = this.getCategoryOptionOverride(category.key, option.key);
        return override ? this.presentOption(option, override) : option;
      }).sort((a, b) => (a.sortOrder ?? 100) - (b.sortOrder ?? 100) || naturalCompare(a.label, b.label));
    }

    getCategoryOptionOverride(categoryKey, optionKey) {
      const scoped = this.config.optionBlocks.list.find(option =>
        option.key === optionKey && this.optionTargetsCategory(option, categoryKey)
      );
      if (scoped) return scoped;
      const scopes = this.scopesForCategory(categoryKey);
      return this.getOptionOverride(optionKey, scopes);
    }

    scopesForCategory(categoryKey) {
      const category = this.getCategory(categoryKey);
      if (!category) return ["any"];
      if (category.dataSource === "catalog_select") return ["category", "any"];
      const field = this.catalogFieldForCategory(category);
      if (field === "finishKey") return ["finish", "any"];
      if (field === this.config.primaryMaterialKey || field === "wood") return ["wood", "any"];
      return ["any"];
    }

    presentOption(base, override) {
      return {
        ...base,
        label: override.label || base.label,
        description: override.description || base.description,
        thumbnailImage: override.thumbnailImage || base.thumbnailImage || "",
        previewImage: override.previewImage || base.previewImage || "",
        previewImage2: override.previewImage2 || base.previewImage2 || "",
        swatchImage: override.thumbnailImage || base.swatchImage || base.thumbnailImage || "",
        frontImage: override.previewImage || base.frontImage || base.previewImage || "",
        backImage: override.previewImage2 || base.backImage || base.previewImage2 || "",
        name: override.label || base.label || base.name,
        defaultOption: override.defaultOption ?? base.defaultOption,
        sortOrder: override.sortOrder ?? base.sortOrder
      };
    }

    shouldIgnoreUpstreamCategory(currentCategory, upstreamCategory) {
      if (!currentCategory || !upstreamCategory) return false;
      if (!this.categoryInheritsPrevious(currentCategory)) return true;
      return (currentCategory.ignoreUpstreamCategoryKeys || []).includes(upstreamCategory.key);
    }

    rowScopeField(category) {
      if (!category) return "";
      return String(category.rowFilterField || category.scopeField || "").trim();
    }

    rowScopeValues(row, field) {
      if (!row || !field) return [];
      const value = row[field];
      if (Array.isArray(value)) return value.map(item => String(item || "").trim()).filter(Boolean);
      const text = String(value || "").trim();
      return text ? [text] : [];
    }

    categoryRowMatchesScope(category, row) {
      if (!category || !row) return true;
      const field = this.rowScopeField(category);
      const includeValues = Array.isArray(category.includeValues) ? category.includeValues : (Array.isArray(category.includeGroups) ? category.includeGroups : []);
      const excludeValues = Array.isArray(category.excludeValues) ? category.excludeValues : (Array.isArray(category.excludeGroups) ? category.excludeGroups : []);
      if (!field || (!includeValues.length && !excludeValues.length)) return true;

      const rowValues = this.rowScopeValues(row, field);
      if (!rowValues.length) return !includeValues.length;

      const normalizedRowValues = rowValues.flatMap(value => [value, this.normalizeFilterMatchKey(value), normalizeAddonCategoryKey(value)]);
      const matchesConfiguredValue = configuredValue => {
        const normalizedConfiguredValue = [configuredValue, this.normalizeFilterMatchKey(configuredValue), normalizeAddonCategoryKey(configuredValue)];
        return normalizedConfiguredValue.some(value => normalizedRowValues.includes(value));
      };

      if (includeValues.length && !includeValues.some(matchesConfiguredValue)) return false;
      if (excludeValues.length && excludeValues.some(matchesConfiguredValue)) return false;
      return true;
    }

    scopeRowsForCategory(category, rows) {
      if (!category) return rows;
      return rows.filter(row => this.categoryRowMatchesScope(category, row));
    }

    catalogRowsBeforeCategory(categoryKey, { requirePriorSelections = false } = {}) {
      const currentCategory = this.getCategory(categoryKey);
      const choosingSeries = this.catalogFieldForCategory(currentCategory) === "series";
      let rows = choosingSeries && !String(this.config.defaultSeriesKey || "").trim()
        ? this.catalog
        : this.catalogRowsForActiveSeries();
      for (const category of this.getCategories()) {
        if (category.key === categoryKey) break;
        if (category.dataSource === "catalog_field") {
          if (this.shouldIgnoreUpstreamCategory(currentCategory, category)) continue;
          let value = this.getCategorySelection(category.key);
          const field = this.catalogFieldForCategory(category);
          if (!value || (Array.isArray(value) && !value.length)) {
            if (requirePriorSelections) {
              value = this.getResolvedCategorySelection(category);
              if (!value || (Array.isArray(value) && !value.length)) return [];
            } else {
              continue;
            }
          }
          const values = Array.isArray(value) ? value : [value];
          rows = rows.filter(row => values.includes(row[field]));
        }
      }
      return this.scopeRowsForCategory(currentCategory, rows).map(row => this.applyCatalogRowOverrides(row));
    }

    priorCategoriesReady(categoryKey) {
      const currentCategory = this.getCategory(categoryKey);
      if (!String(this.config.defaultSeriesKey || "").trim() && currentCategory !== this.getSeriesCategory() && !this.state.series) return false;
      for (const category of this.getCategories()) {
        if (category.key === categoryKey) return true;
        if (category.dataSource !== "catalog_field") continue;
        if (this.shouldIgnoreUpstreamCategory(currentCategory, category)) continue;
        const value = this.getResolvedCategorySelection(category);
        if (!value || (Array.isArray(value) && !value.length)) return false;
      }
      return true;
    }

    getDimensionValue(fieldName) {
      const category = this.getCategories().find(item =>
        item.dataSource === "catalog_field" && item.catalogField === fieldName
      );
      if (!category) return null;
      const value = this.getCategorySelection(category.key);
      return Array.isArray(value) ? value[0] : value;
    }

    getSelectedOptionKeysByCategory() {
      const map = new Map();
      this.getCategories().forEach(category => {
        if (category.dataSource !== "catalog_field" && category.dataSource !== "manual") return;
        const value = this.getCategorySelection(category.key);
        if (value == null || value === "") return;
        map.set(category.key, value);
      });
      return map;
    }

    getAllSelectedOptionKeys() {
      const keys = new Set();
      this.getSelectedOptionKeysByCategory().forEach(value => {
        if (Array.isArray(value)) value.forEach(key => keys.add(key));
        else keys.add(value);
      });
      return keys;
    }

    optionKeyMatchesCategory(categoryKey, optionKey, selectionsByCategory) {
      if (!categoryKey) return true;
      const value = selectionsByCategory.get(categoryKey);
      if (value == null || value === "") return false;
      return Array.isArray(value) ? value.includes(optionKey) : value === optionKey;
    }

    getActiveImageCombo() {
      const selectedKeys = this.getAllSelectedOptionKeys();
      const selectionsByCategory = this.getSelectedOptionKeysByCategory();
      return (this.config.imageCombos || []).find(combo => {
        if (!selectedKeys.has(combo.optionKey1) || !selectedKeys.has(combo.optionKey2)) return false;
        return this.optionKeyMatchesCategory(combo.categoryKey1, combo.optionKey1, selectionsByCategory) &&
          this.optionKeyMatchesCategory(combo.categoryKey2, combo.optionKey2, selectionsByCategory);
      }) || null;
    }

    photosFromImageCombo(combo) {
      if (!combo?.images?.length) return [];
      const label = combo.label || `${combo.optionKey1} + ${combo.optionKey2}`;
      return combo.images.map((src, index) => ({
        src,
        alt: label,
        label: index === 0 ? label : `${label} view ${index + 1}`
      }));
    }

    parseShellParam(value) {
      return String(value || "").split(",").filter(Boolean).map(part => {
        const exactMatch = part.match(/^(.+)@(\d+)$/);
        if (exactMatch) {
          const catalogId = String(exactMatch[1] || "").trim();
          const quantity = Math.max(1, Number.parseInt(exactMatch[2], 10) || 1);
          const catalogParts = catalogId.split("|");
          return {
            catalogId,
            drumType: String(catalogParts[3] || "").trim(),
            sizeKey: String(catalogParts[4] || "").trim(),
            quantity,
            unavailable: false
          };
        }
        const match = part.match(/^([^:]+):(.+)x(\d+)$/);
        if (!match) return null;
        return {
          catalogId: "",
          drumType: String(match[1] || "").trim(),
          sizeKey: match[2],
          quantity: Math.max(1, Number.parseInt(match[3], 10) || 1),
          unavailable: false
        };
      }).filter(Boolean);
    }

    restoreSelectedShellsAfterCatalogLoad() {
      this.getCatalogSelectCategories().forEach(category => {
        const items = this.getCategoryCatalogSelection(category.key);
        this.state.catalogSelections[category.key] = items.map(item => {
          if (item.catalogId && this.catalogMap.has(item.catalogId)) {
            const existing = this.catalogMap.get(item.catalogId);
            // Drop items that belong to a different catalog_select scope (e.g. shells mis-read into snare).
            if (existing && (!this.categoryRowMatchesScope(category, existing) || existing.series !== this.state.series)) return null;
            if (existing) return item;
          }
          const row = this.findCatalogRow({
            categoryKey: category.key,
            drumType: item.drumType,
            sizeKey: item.sizeKey
          });
          if (row) return { catalogId: row.catalogId, quantity: item.quantity, unavailable: false };
          // If we can tell this drum type is out of scope for this step, drop it instead of showing unavailable.
          if (item.drumType && !this.categoryRowMatchesScope(category, { drumType: item.drumType })) {
            return null;
          }
          const unavailable = { ...item, catalogId: this.unavailableId(item), unavailable: true };
          this.warn("selected catalog item becomes unavailable after upstream change", unavailable);
          return unavailable;
        }).filter(Boolean);
      });
    }

    catalogItemMatchesCategory(category, item) {
      if (!category || !item) return false;
      const row = item.catalogId ? this.catalogById(item.catalogId) : null;
      if (row) return row.series === this.state.series && this.categoryRowMatchesScope(category, row);
      if (item.drumType) {
        return this.categoryRowMatchesScope(category, { drumType: item.drumType });
      }
      // Until the catalog loads, keep items that were stored under this category key.
      return true;
    }

    pruneCatalogSelectionsToCategoryScope() {
      if (!this.state.catalogSelections) return;
      this.getCatalogSelectCategories().forEach(category => {
        const items = this.getCategoryCatalogSelection(category.key);
        if (!items.length) return;
        this.state.catalogSelections[category.key] = items.filter(item =>
          this.catalogItemMatchesCategory(category, item)
        );
      });
    }

    getCatalogSelectCategories() {
      return this.getCategories().filter(category => category.dataSource === "catalog_select");
    }

    unavailableId(item) {
      return [
        this.state.series,
        this.getDimensionValue("wood") || this.getDimensionValue(this.config.primaryMaterialKey),
        this.getDimensionValue("finishKey"),
        item.drumType,
        item.sizeKey
      ].join("|");
    }

    render() {
      const heroCaption = String(this.config.heroCaption || "").trim();
      const visibleHeroCaption = heroCaption === "__empty__" ? "" : heroCaption;
      const app = this.getAppMount();

      app.innerHTML = `
        <div class="sdkc-shell ddkb-shell">
          <section class="sdkc-atelier" data-motion-surface>
            <div class="sdkc-hero-copy">
              <p class="sdkc-kicker">${escapeHtml(this.config.eyebrow)}</p>
              <h1>${escapeHtml(this.config.heroHeadline)}</h1>
              <div class="sdkc-hero-points" aria-label="Value propositions">
                ${this.config.heroPoint1 ? `<span>${escapeHtml(this.config.heroPoint1)}</span>` : ""}
                ${this.config.heroPoint2 ? `<span>${escapeHtml(this.config.heroPoint2)}</span>` : ""}
                ${this.config.heroPoint3 ? `<span>${escapeHtml(this.config.heroPoint3)}</span>` : ""}
              </div>
            </div>
            <figure class="sdkc-hero-media">
              <img src="${escapeHtml(this.config.heroImage)}" alt="${escapeHtml(this.config.heroImageAlt)}" loading="eager">
              <figcaption>
                <strong>${escapeHtml(this.config.productTitle)}</strong>
                ${visibleHeroCaption ? `<small>${escapeHtml(visibleHeroCaption)}</small>` : ""}
              </figcaption>
            </figure>
          </section>

          <main class="sdkc-builder" data-builder>
            <section class="sdkc-stage" aria-label="Drum kit preview">
              <div class="sdkc-stage-toolbar">
                <div>
                  <span data-stage-finish-category></span>
                  <strong data-stage-finish></strong>
                  <small class="sdkc-stage-shell-size-format">Depth x Diameter<br>(eg. 14 x 18)</small>
                </div>
                <div class="sdkc-drum-lineup sdkc-drum-lineup-desktop" data-drum-lineup aria-live="polite"></div>
              </div>
              <div class="sdkc-photo-frame">
                <img class="sdkc-kit-photo" data-kit-photo src="${escapeHtml(this.config.heroImage)}" alt="${escapeHtml(this.config.heroImageAlt)}">
                <button type="button" class="sdkc-gallery-arrow sdkc-gallery-prev" data-gallery-dir="-1" aria-label="Previous kit photo">&lsaquo;</button>
                <button type="button" class="sdkc-gallery-arrow sdkc-gallery-next" data-gallery-dir="1" aria-label="Next kit photo">&rsaquo;</button>
              </div>
              <div class="sdkc-drum-lineup sdkc-drum-lineup-mobile" data-drum-lineup aria-live="polite"></div>
              <p class="sdkc-stage-disclaimer">
                <span class="sdkc-stage-disclaimer-full">Please note &mdash; all tom holders, cymbals and hardware are not included. Only the items selected and listed on the Review page are included in this estimate.</span>
                <span class="sdkc-stage-disclaimer-mobile">Tom holder, cymbals and hardware not included</span>
                <button type="button" class="sdkc-stage-disclaimer-dismiss" data-dismiss-hardware-notice aria-label="Dismiss hardware notice">&times;</button>
              </p>
            </section>

            <aside class="sdkc-detail-panel" aria-label="Configure selected step">
              <div class="sdkc-detail-top">
                <div class="sdkc-detail-top-title">
                  <span data-detail-kicker></span>
                  <strong data-detail-title></strong>
                  <small class="sdkc-shell-size-format" data-shell-size-format hidden>Depth x Diameter (14 x 18)</small>
                </div>
                <button
                  type="button"
                  class="sdkc-toggle-preview"
                  data-toggle-mobile-preview
                  aria-pressed="false"
                  aria-label="Hide kit image"
                >Hide image</button>
                <div class="sdkc-detail-top-meta">
                  <small data-progress-copy></small>
                  <div class="sdkc-detail-top-actions">
                    <button type="button" class="sdkc-rail-toggle-inline" data-toggle-rail></button>
                    <button type="button" class="sdkc-reset-builder" data-reset-builder aria-label="Reset builder">
                      <span class="sdkc-reset-label-full">Reset builder</span>
                      <span class="sdkc-reset-label-short">Reset</span>
                    </button>
                  </div>
                </div>
              </div>
              <section class="sdkc-panel-stack" data-panels></section>
            </aside>

            <div class="sdkc-step-rail-wrap">
              <nav class="sdkc-step-rail" data-step-rail aria-label="Build steps"></nav>
            </div>
          </main>

          <div class="sdkc-quote-bar" data-quote-bar>
            <button type="button" class="sdkc-quote-mobile-toggle" data-toggle-mobile-quote aria-expanded="false" aria-label="Expand build summary">&#8963;</button>
            <div class="sdkc-quote-lead">
              <span class="sdkc-quote-eyebrow sdkc-quote-points-eyebrow" data-quote-eyebrow></span>
              <strong class="sdkc-quote-summary" data-quote-summary></strong>
            </div>
            <div class="sdkc-quote-money">
              <span class="sdkc-quote-eyebrow sdkc-quote-finance-eyebrow" data-finance-summary></span>
              <strong class="sdkc-quote-total" data-quote-total></strong>
            </div>
            <button type="button" class="sdkc-button sdkc-button-secondary" data-share-build>${escapeHtml(this.config.shareButtonLabel)}</button>
            <button type="button" class="sdkc-button sdkc-button-primary" data-request-quote>${escapeHtml(this.config.requestButtonLabel)}</button>
          </div>

          <dialog class="sdkc-photo-viewer" data-photo-viewer aria-label="Enlarged kit photos">
            <div class="sdkc-photo-viewer-surface" data-photo-viewer-surface>
              <button type="button" class="sdkc-photo-viewer-close" data-photo-viewer-close aria-label="Close enlarged photo">&times;</button>
              <img data-photo-viewer-image src="" alt="">
              <button type="button" class="sdkc-photo-viewer-arrow sdkc-photo-viewer-prev" data-gallery-dir="-1" aria-label="Previous enlarged kit photo">&lsaquo;</button>
              <button type="button" class="sdkc-photo-viewer-arrow sdkc-photo-viewer-next" data-gallery-dir="1" aria-label="Next enlarged kit photo">&rsaquo;</button>
              <div class="sdkc-photo-viewer-meta">
                <strong data-photo-viewer-label></strong>
                <span data-photo-viewer-count></span>
              </div>
            </div>
          </dialog>

          <dialog class="sdkc-modal" data-quote-modal>
            <form method="dialog">
              <button class="sdkc-modal-close" aria-label="Close quote modal">x</button>
            </form>
            <div class="sdkc-modal-body">
              <div data-quote-request-intro>
                <span>Quote request</span>
                <h2>Send this custom build to the Drumland team.</h2>
              </div>
              <form
                class="sdkc-quote-form"
                data-quote-form
                data-shopify-captcha="true"
                action="/contact"
                method="post"
                accept-charset="UTF-8"
              >
                <input type="hidden" name="form_type" value="contact">
                <input type="hidden" name="utf8" value="&#10003;">
                <input type="hidden" name="return_to" data-return-to>
                <input type="hidden" name="contact[Subject]" data-quote-subject>
                <input type="hidden" name="contact[Ref #]" data-quote-number>
                <input type="hidden" name="contact[Estimated Total]" data-total-hidden>
                <input type="hidden" name="contact[body]" data-body-hidden>
                <div class="sdkc-form-grid">
                  <label class="sdkc-field sdkc-field-wide">
                    <span>Name *</span>
                    <input type="text" name="contact[name]" required autocomplete="name" placeholder="Your name">
                  </label>
                  <label class="sdkc-field sdkc-field-wide">
                    <span>Email *</span>
                    <input type="email" name="contact[email]" required autocomplete="email" placeholder="you@example.com">
                  </label>
                  <label class="sdkc-field sdkc-field-wide">
                    <span>Phone</span>
                    <input type="tel" name="contact[phone]" autocomplete="tel" placeholder="Phone number">
                  </label>
                </div>
                <label class="sdkc-field">
                  <span>Notes</span>
                  <textarea name="contact[Customer Notes]" data-customer-notes rows="4" placeholder="Tell us anything else about your custom kit."></textarea>
                </label>
                <label class="sdkc-copy-request">
                  <input type="checkbox" name="contact[Interested in Financing]" value="Yes">
                  <span>I am interested in financing this build</span>
                </label>
                <label class="sdkc-field">
                  <span>Build summary</span>
                  <textarea data-build-output readonly></textarea>
                </label>
                <p class="sdkc-quote-form-status" data-quote-status hidden role="alert"></p>
                <div class="sdkc-modal-actions">
                  <button type="submit" class="sdkc-button sdkc-button-primary" data-send-quote>Send request</button>
                </div>
              </form>
              <div class="sdkc-submit-confirmation" data-submit-confirmation hidden role="status" tabindex="-1">
                <div class="sdkc-confirmation-sheet" data-confirmation-sheet>
                  <div class="sdkc-confirmation-intro">
                    <p class="sdkc-confirmation-eyebrow">Request received</p>
                    <h2 data-confirmation-greeting>Hi there,</h2>
                    <p data-confirmation-message>
                      Your custom kit request has been submitted. A member of the Drumland team will review your build and reach out to confirm pricing, availability, and next steps.
                    </p>
                  </div>
                  <dl class="sdkc-confirmation-meta">
                    <div>
                      <dt>Ref #</dt>
                      <dd data-confirmation-quote>&mdash;</dd>
                    </div>
                    <div>
                      <dt>Approx. price</dt>
                      <dd data-confirmation-price>&mdash;</dd>
                    </div>
                    <div class="sdkc-confirmation-meta-wide">
                      <dt>Build</dt>
                      <dd data-confirmation-build>&mdash;</dd>
                    </div>
                    <div class="sdkc-confirmation-meta-wide">
                      <dt>Your build link</dt>
                      <dd>
                        <a data-confirmation-url href="#" target="_blank" rel="noopener"></a>
                      </dd>
                    </div>
                  </dl>
                  <div class="sdkc-confirmation-contact" data-confirmation-contact></div>
                  <p class="sdkc-confirmation-footnote">
                    This is a planning estimate only. Final pricing, taxes, shipping, and payment terms will be confirmed before any order is placed.
                  </p>
                </div>
                <div class="sdkc-confirmation-actions">
                  <button type="button" class="sdkc-button sdkc-button-secondary" data-confirmation-pdf>Save my build</button>
                  <button type="button" class="sdkc-button sdkc-button-secondary" data-confirmation-copy-url>Copy build URL</button>
                  <button type="button" class="sdkc-button sdkc-button-primary" data-confirmation-new-build>Start a new build</button>
                </div>
              </div>
            </div>
          </dialog>
        </div>
      `;
    }

    cacheElements() {
      this.elements.panels = this.root.querySelector("[data-panels]");
      this.elements.builder = this.root.querySelector("[data-builder]");
      this.elements.stepRail = this.root.querySelector("[data-step-rail]");
      this.elements.total = this.root.querySelectorAll("[data-quote-total]");
      this.elements.stageFinish = this.root.querySelector("[data-stage-finish]");
      this.elements.stageFinishCategory = this.root.querySelector("[data-stage-finish-category]");
      this.elements.drumLineups = this.root.querySelectorAll("[data-drum-lineup]");
      this.elements.photo = this.root.querySelector("[data-kit-photo]");
      this.elements.galleryPrev = this.root.querySelector("[data-gallery-dir='-1']");
      this.elements.galleryNext = this.root.querySelector("[data-gallery-dir='1']");
      this.elements.photoViewer = this.root.querySelector("[data-photo-viewer]");
      this.elements.photoViewerSurface = this.root.querySelector("[data-photo-viewer-surface]");
      this.elements.photoViewerImage = this.root.querySelector("[data-photo-viewer-image]");
      this.elements.photoViewerLabel = this.root.querySelector("[data-photo-viewer-label]");
      this.elements.photoViewerCount = this.root.querySelector("[data-photo-viewer-count]");
      this.elements.photoViewerPrev = this.root.querySelector(".sdkc-photo-viewer-prev");
      this.elements.photoViewerNext = this.root.querySelector(".sdkc-photo-viewer-next");
      this.elements.detailKicker = this.root.querySelector("[data-detail-kicker]");
      this.elements.detailTitle = this.root.querySelector("[data-detail-title]");
      this.elements.shellSizeFormat = this.root.querySelector("[data-shell-size-format]");
      this.elements.progressCopy = this.root.querySelector("[data-progress-copy]");
      this.elements.quoteEyebrow = this.root.querySelector("[data-quote-eyebrow]");
      this.elements.quoteSummary = this.root.querySelector("[data-quote-summary]");
      this.elements.financeSummary = this.root.querySelector("[data-finance-summary]");
      this.elements.modal = this.root.querySelector("[data-quote-modal]");
      this.elements.quoteForm = this.root.querySelector("[data-quote-form]");
      this.elements.returnTo = this.root.querySelector("[data-return-to]");
      this.elements.quoteSubject = this.root.querySelector("[data-quote-subject]");
      this.elements.quoteNumber = this.root.querySelector("[data-quote-number]");
      this.elements.totalHidden = this.root.querySelector("[data-total-hidden]");
      this.elements.bodyHidden = this.root.querySelector("[data-body-hidden]");
      this.elements.customerNotes = this.root.querySelector("[data-customer-notes]");
      this.elements.output = this.root.querySelector("[data-build-output]");
      this.elements.quoteStatus = this.root.querySelector("[data-quote-status]");
      this.elements.quoteRequestIntro = this.root.querySelector("[data-quote-request-intro]");
      this.elements.submitConfirmation = this.root.querySelector("[data-submit-confirmation]");
      this.elements.confirmationSheet = this.root.querySelector("[data-confirmation-sheet]");
      this.elements.confirmationGreeting = this.root.querySelector("[data-confirmation-greeting]");
      this.elements.confirmationMessage = this.root.querySelector("[data-confirmation-message]");
      this.elements.confirmationQuote = this.root.querySelector("[data-confirmation-quote]");
      this.elements.confirmationPrice = this.root.querySelector("[data-confirmation-price]");
      this.elements.confirmationBuild = this.root.querySelector("[data-confirmation-build]");
      this.elements.confirmationUrl = this.root.querySelector("[data-confirmation-url]");
      this.elements.confirmationContact = this.root.querySelector("[data-confirmation-contact]");
      this.elements.confirmationPdf = this.root.querySelector("[data-confirmation-pdf]");
      this.elements.confirmationCopyUrl = this.root.querySelector("[data-confirmation-copy-url]");
      this.elements.confirmationNewBuild = this.root.querySelector("[data-confirmation-new-build]");
      this.updatePhotoTriggerAccessibility();
    }

    bindEvents() {
      this.handleViewportChange = () => {
        if (this.viewportTick) return;
        this.viewportTick = window.requestAnimationFrame(() => {
          this.viewportTick = 0;
          this.updatePhotoTriggerAccessibility();
          this.syncMobilePreviewVisibility();
          this.updateStickyOffset();
          this.syncMobileScrollHints();
        });
      };

      this.root.addEventListener("click", event => {
        const panelButton = event.target.closest("[data-panel-toggle]");
        const categoryOptionButton = event.target.closest("[data-category-option]");
        const shellButton = event.target.closest("[data-shell-action]");
        const qtyButton = event.target.closest("[data-shell-qty]");
        const removeSelectedShellButton = event.target.closest("[data-remove-selected-shell]");
        const removeUnavailableButton = event.target.closest("[data-remove-unavailable-shell]");
        const addQuoteButton = event.target.closest("[data-add-quote]");
        const buyButton = event.target.closest("[data-buy-now]");
        const previewAddonButton = event.target.closest("[data-preview-addon]");
        const nextButton = event.target.closest("[data-next-step]");
        const galleryButton = event.target.closest("[data-gallery-dir]");
        const shareButton = event.target.closest("[data-share-build]");
        const requestButton = event.target.closest("[data-request-quote]");
        const railToggleButton = event.target.closest("[data-toggle-rail]");
        const showMoreAddonsButton = event.target.closest("[data-show-more-addons]");
        const addonFilterBtn = event.target.closest("[data-addon-filter]");
        const categoryFilterBtn = event.target.closest("[data-category-filter]");
        const dismissSelectionNoticeButton = event.target.closest("[data-dismiss-selection-notice]");
        const mobileQuoteToggleButton = event.target.closest("[data-toggle-mobile-quote]");
        const mobilePreviewToggleButton = event.target.closest("[data-toggle-mobile-preview]");
        const resetBuilderButton = event.target.closest("[data-reset-builder]");
        const dismissHardwareNoticeButton = event.target.closest("[data-dismiss-hardware-notice]");
        const photoViewerCloseButton = event.target.closest("[data-photo-viewer-close]");
        const kitPhoto = event.target.closest("[data-kit-photo]");

        if (panelButton) {
          this.setActiveStep(panelButton.dataset.panelToggle);
          return;
        }
        if (categoryOptionButton) {
          this.selectCategoryOption(categoryOptionButton.dataset.categoryKey, categoryOptionButton.dataset.categoryOption);
        }
        if (shellButton) {
          this.toggleCatalogItem(shellButton.dataset.shellCategory, shellButton.dataset.shellAction);
        }
        if (qtyButton) {
          this.adjustCatalogItemQuantity(qtyButton.dataset.shellCategory, qtyButton.dataset.shellQty, Number(qtyButton.dataset.qtyDelta));
        }
        if (removeSelectedShellButton) {
          this.removeUnavailableCatalogItem(
            removeSelectedShellButton.dataset.shellCategory,
            removeSelectedShellButton.dataset.removeSelectedShell
          );
        }
        if (removeUnavailableButton) {
          this.removeUnavailableCatalogItem(removeUnavailableButton.dataset.shellCategory, removeUnavailableButton.dataset.removeUnavailableShell);
        }
        if (previewAddonButton) {
          this.previewAddOn(previewAddonButton.dataset.previewAddon);
          return;
        }
        if (addQuoteButton) this.toggleAddOn(addQuoteButton.dataset.addQuote);
        if (buyButton) this.buyAddOn(buyButton.dataset.buyNow);
        if (nextButton) this.goToNextStep();
        if (galleryButton) this.changePhoto(Number(galleryButton.dataset.galleryDir));
        if (shareButton) this.shareBuild();
        if (requestButton) this.openQuoteModal();
        if (railToggleButton) this.toggleRail();
        if (showMoreAddonsButton) this.toggleAddonRow(showMoreAddonsButton.dataset.showMoreAddons);
        if (addonFilterBtn) {
          this.state.activeAddonFilter = addonFilterBtn.dataset.addonFilter || "";
          this.update();
        }
        if (categoryFilterBtn) {
          this.setCategoryFilter(categoryFilterBtn.dataset.categoryKey, categoryFilterBtn.dataset.categoryFilter || "");
          this.update();
        }
        if (dismissSelectionNoticeButton) {
          this.state.selectionChangeNotice = "";
          this.update();
        }
        if (mobileQuoteToggleButton) this.toggleMobileQuoteBar();
        if (mobilePreviewToggleButton) this.toggleMobilePreview();
        if (resetBuilderButton) this.resetBuilder();
        if (dismissHardwareNoticeButton) {
          dismissHardwareNoticeButton.closest(".sdkc-stage-disclaimer")?.classList.add("is-dismissed");
        }
        if (kitPhoto) this.openPhotoViewer();
        if (photoViewerCloseButton || event.target === this.elements.photoViewer) this.closePhotoViewer();
      });

      this.elements.photo?.addEventListener("keydown", event => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        this.openPhotoViewer();
      });

      this.elements.photoViewerSurface?.addEventListener("pointerdown", event => {
        if (event.pointerType === "mouse") return;
        this.photoSwipeStartX = event.clientX;
      });
      this.elements.photoViewerSurface?.addEventListener("pointerup", event => {
        if (this.photoSwipeStartX == null) return;
        const swipeDistance = event.clientX - this.photoSwipeStartX;
        this.photoSwipeStartX = null;
        if (Math.abs(swipeDistance) < 50) return;
        this.changePhoto(swipeDistance < 0 ? 1 : -1);
      });
      this.elements.photoViewerSurface?.addEventListener("pointercancel", () => {
        this.photoSwipeStartX = null;
      });

      // Populate the dynamic fields, then let Shopify own the native CAPTCHA-protected POST.
      this.elements.quoteForm?.addEventListener("submit", event => {
        this.submitQuoteForm(event);
      });
      this.elements.confirmationPdf?.addEventListener("click", () => this.printConfirmation({ saveAsPdf: true }));
      this.elements.confirmationCopyUrl?.addEventListener("click", () => this.copyBuildUrlFromConfirmation());
      this.elements.confirmationNewBuild?.addEventListener("click", () => this.startNewBuildFromConfirmation());
      window.addEventListener("scroll", this.handleViewportChange, { passive: true });
      window.addEventListener("resize", this.handleViewportChange);
      window.visualViewport?.addEventListener("resize", this.handleViewportChange);
      window.visualViewport?.addEventListener("scroll", this.handleViewportChange);
    }

    resetBuilder({ skipConfirm = false } = {}) {
      if (!skipConfirm) {
        const confirmed = window.confirm("Reset this builder and remove all current selections?");
        if (!confirmed) return;
      }

      const cleanUrl = new URL(window.location.href);
      const builderParams = new Set([
        "step", "view", "kitView", "series", "wood", "finishGroup", "shellGroup", "shells", "addons",
        QUOTE_RETURN_PARAM, "contact_posted", "form_type"
      ]);

      this.getCategories().forEach(category => {
        builderParams.add(category.key);
        builderParams.add(this.categoryFilterParamName(category));
        const legacyParam = this.legacyParamForCategory(category);
        if (legacyParam) builderParams.add(legacyParam);
      });
      builderParams.forEach(param => param && cleanUrl.searchParams.delete(param));

      [window.localStorage, window.sessionStorage].forEach(storage => {
        try {
          Object.keys(storage).forEach(key => {
            if (/^(ddkb|dream-drum-kit-builder)(?:$|[:._-])/i.test(key)) storage.removeItem(key);
          });
        } catch (error) {
          this.warn("builder storage could not be cleared", error);
        }
      });

      this.confirmationSnapshot = null;
      if (this.elements.modal?.open) this.elements.modal.close();
      this.elements.modal?.classList.remove("is-confirmation");

      window.history.replaceState({}, "", cleanUrl);
      this.state = this.readInitialState();
      this.ensureValidSelections();
      this.update();
      window.history.replaceState({}, "", cleanUrl);
    }

    async copyBuildUrlFromConfirmation() {
      this.updateUrl();
      const url = this.cleanShareUrl();
      const button = this.elements.confirmationCopyUrl;
      const originalLabel = button?.textContent || "Copy build URL";
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
        } else {
          const input = document.createElement("textarea");
          input.value = url;
          input.setAttribute("readonly", "");
          input.style.position = "fixed";
          input.style.left = "-9999px";
          document.body.appendChild(input);
          input.select();
          document.execCommand("copy");
          document.body.removeChild(input);
        }
        if (button) {
          button.textContent = "Link copied";
          window.setTimeout(() => {
            if (button.isConnected) button.textContent = originalLabel;
          }, 1800);
        }
      } catch (error) {
        this.warn("could not copy build URL", error);
        if (button) button.textContent = "Copy failed";
        window.setTimeout(() => {
          if (button?.isConnected) button.textContent = originalLabel;
        }, 1800);
      }
    }

    startNewBuildFromConfirmation() {
      this.resetBuilder({ skipConfirm: true });
    }

    fetchJson(url, fallback) {
      return fetchJson(url, fallback);
    }

    normalizeCatalog(items) {
      const seen = new Set();
      const source = Array.isArray(items) ? items : [];
      const normalized = [];

      source.forEach((raw, index) => {
        const woodOptions = this.normalizeWoodOptions(raw);
        woodOptions.forEach(woodOption => {
          const extraFields = {};
          Object.entries(raw || {}).forEach(([key, value]) => {
            if ([
              "series",
              "seriesLabel",
              "brand",
              "wood",
              "woodLabels",
              "finishKey",
              "finishName",
              "finishFamily",
              "finishFamilyLabel",
              "drumType",
              "drumTypeLabel",
              "sizeKey",
              "sizeLabel",
              "stockNumber",
              "modelNumber",
              "price",
              "imageKey",
              "sortOrder",
              "status"
            ].includes(key)) return;
            extraFields[key] = value;
          });

          const row = {
          ...extraFields,
          series: String(raw.series || "").trim(),
          seriesLabel: String(raw.seriesLabel || raw.series || "").trim(),
          wood: woodOption.key,
          woodLabel: woodOption.label,
          finishKey: String(raw.finishKey || "").trim(),
          finishName: String(raw.finishName || raw.finishKey || "").trim(),
          finishFamily: String(raw.finishFamily || "").trim(),
          finishFamilyLabel: String(raw.finishFamilyLabel || "").trim(),
          drumType: String(raw.drumType || "").trim(),
          drumTypeLabel: String(raw.drumTypeLabel || raw.drumType || "").trim(),
          sizeKey: String(raw.sizeKey || "").trim(),
          sizeLabel: String(raw.sizeLabel || raw.sizeKey || "").trim(),
          stockNumber: String(raw.stockNumber || "").trim(),
          modelNumber: String(raw.modelNumber || "").trim(),
          price: Number(raw.price),
          imageKey: String(raw.imageKey || "").trim(),
          sortOrder: toNumber(raw.sortOrder || index),
          status: String(raw.status || "").trim()
        };

        const missing = ["series", "wood", "finishKey", "drumType", "sizeKey", "modelNumber"].filter(key => !row[key]);
        missing.forEach(key => this.warn(`row missing ${key}`, raw));
        if (!Number.isFinite(row.price)) this.warn("row missing price", raw);
        if (missing.length || !Number.isFinite(row.price)) return;

        row.price = Math.max(0, Math.round(row.price));
        row.catalogId = this.catalogId(row);
        if (seen.has(row.catalogId)) {
          this.warn("duplicate catalog IDs", row.catalogId);
          return;
        }
        seen.add(row.catalogId);
        normalized.push(row);
        });
      });

      return normalized.sort((a, b) => a.sortOrder - b.sortOrder || naturalCompare(a.sizeLabel, b.sizeLabel));
    }

    normalizeWoodOptions(raw) {
      const materialKey = this.config.primaryMaterialKey || "wood";
      const sourceValue = raw[materialKey] ?? raw.wood ?? raw.material;
      const woods = Array.isArray(sourceValue) ? sourceValue : [sourceValue];
      const labels = raw[`${materialKey}Labels`] || raw.woodLabels || raw.materialLabels || raw.woodLabel || raw.materialLabel || {};

      return woods.map(wood => {
        const key = String(wood || "").trim();
        let label = "";
        if (Array.isArray(labels)) label = labels[woods.indexOf(wood)] || "";
        else if (labels && typeof labels === "object") label = labels[key] || "";
        else label = String(labels || "").trim();

        return {
          key,
          label: label || titleFromKey(key)
        };
      }).filter(wood => wood.key);
    }

    catalogId(item) {
      return [item.series, item.wood, item.finishKey, item.drumType, item.sizeKey].join("|");
    }

    catalogById(id) {
      const row = this.catalogMap.get(id) || null;
      return row ? this.applyCatalogRowOverrides(row) : null;
    }

    catalogForCurrentSelection() {
      const selectCategory = this.getCatalogSelectCategories()[0];
      if (selectCategory) return this.catalogRowsBeforeCategory(selectCategory.key, { requirePriorSelections: true });
      return this.catalogRowsForActiveSeries()
        .map(row => this.applyCatalogRowOverrides(row));
    }

    getAvailableWoodOptions() {
      const map = new Map();
      this.catalogRowsForActiveSeries().forEach(row => {
        const key = row.wood;
        if (!key || map.has(key)) return;
        map.set(key, {
          key,
          label: row.woodLabel || titleFromKey(key)
        });
      });

      return [...map.values()].map(wood => {
        const option = this.getOptionOverride(wood.key, ["wood", "any"]);
        if (!option) return wood;
        const mapped = this.mapOptionToMaterialOverride(option);
        return {
          ...wood,
          ...mapped,
          label: mapped.label || wood.label
        };
      }).sort((a, b) => (a.sortOrder ?? 100) - (b.sortOrder ?? 100) || naturalCompare(a.label, b.label));
    }

    mapOptionToMaterialOverride(option) {
      return {
        label: option.label || "",
        description: option.description || "",
        thumbnailImage: option.thumbnailImage || "",
        previewImage: option.previewImage || "",
        previewImage2: option.previewImage2 || "",
        defaultOption: option.defaultOption,
        sortOrder: option.sortOrder
      };
    }

    getFinishBlocks() {
      const catalogFinishes = this.getCatalogFinishOptions();
      return catalogFinishes.map(finish => {
        const block = this.config.finishBlocks.find(item => item.key === finish.key);
        const option = this.getOptionOverride(finish.key, ["finish", "any"]);
        if (!block && !option) return finish;
        const optionMapped = option ? this.mapOptionToFinishOverride(option) : {};
        return {
          ...finish,
          ...block,
          ...optionMapped,
          name: optionMapped.name || block?.name || finish.name,
          familyLabel: block?.familyLabel || finish.familyLabel,
          description: optionMapped.description || block?.description || finish.description,
          swatchImage: optionMapped.swatchImage || block?.swatchImage || finish.swatchImage,
          frontImage: optionMapped.frontImage || block?.frontImage || finish.frontImage,
          backImage: optionMapped.backImage || block?.backImage || finish.backImage,
          defaultFinish: optionMapped.defaultFinish || block?.defaultFinish || finish.defaultFinish,
          sortOrder: optionMapped.sortOrder ?? block?.sortOrder ?? finish.sortOrder
        };
      }).sort(sortBySortOrderThenName);
    }

    mapOptionToFinishOverride(option) {
      return {
        name: option.label || "",
        description: option.description || "",
        swatchImage: option.thumbnailImage || "",
        frontImage: option.previewImage || "",
        backImage: option.previewImage2 || "",
        defaultFinish: option.defaultOption,
        sortOrder: option.sortOrder
      };
    }

    getCatalogFinishOptions() {
      const map = new Map();
      this.catalog
        .filter(row => row.series === this.state.series && row.wood === (this.getDimensionValue("wood") || this.getDimensionValue(this.config.primaryMaterialKey)))
        .forEach(row => {
          if (map.has(row.finishKey)) return;
          map.set(row.finishKey, {
            id: `catalog-finish-${row.finishKey}`,
            name: row.finishName || titleFromKey(row.finishKey),
            key: row.finishKey,
            familyLabel: row.finishFamilyLabel || row.finishFamily || "Finish",
            description: "",
            swatchImage: "",
            frontImage: "",
            backImage: "",
            defaultFinish: false,
            sortOrder: row.sortOrder || 100
          });
        });
      return [...map.values()].sort(sortBySortOrderThenName);
    }

    getSelectedFinishBlock() {
      return this.getFinishBlocks().find(block => block.key === this.state.finish) || null;
    }

    getAvailableShellRows() {
      return this.catalogForCurrentSelection();
    }

    getShellCategoryBlocks() {
      const catalogBlocks = this.getCatalogShellCategoryOptions();
      return catalogBlocks.map(category => {
        const block = this.config.shellCategoryBlocks.find(item => item.drumTypeKey === category.drumTypeKey);
        const option = this.getOptionOverride(category.drumTypeKey, ["category", "any"]);
        if (!block && !option) return category;
        return {
          ...category,
          ...block,
          label: option?.label || block?.label || category.label,
          description: option?.description || block?.description || category.description,
          sortOrder: option?.sortOrder ?? block?.sortOrder ?? category.sortOrder
        };
      }).sort(sortBySortOrderThenLabel);
    }

    getCatalogShellCategoryOptions() {
      const map = new Map();
      this.catalogForCurrentSelection().forEach(row => {
        if (map.has(row.drumType)) return;
        map.set(row.drumType, {
          id: `catalog-category-${row.drumType}`,
          label: row.drumTypeLabel || titleFromKey(row.drumType),
          drumTypeKey: row.drumType,
          description: "",
          sortOrder: this.defaultDrumTypeSort(row.drumType),
          defaultOpen: true,
          allowQuantity: true
        });
      });
      return [...map.values()].sort(sortBySortOrderThenLabel);
    }

    defaultDrumTypeSort(drumType) {
      const order = { bd: 10, bass: 10, kick: 10, tom: 20, ft: 30, floor: 30, sn: 40, snare: 40 };
      return order[drumType] || 100;
    }

    groupShellRowsByCategoryBlocks(rows) {
      const rowMap = new Map();
      rows.forEach(row => {
        if (!rowMap.has(row.drumType)) rowMap.set(row.drumType, []);
        rowMap.get(row.drumType).push(row);
      });

      return this.getShellCategoryBlocks().map(block => ({
        block,
        rows: (rowMap.get(block.drumTypeKey) || []).slice().sort((a, b) => a.sortOrder - b.sortOrder || naturalCompare(a.sizeLabel, b.sizeLabel))
      }));
    }

    getShellImageForRow(row, category) {
      const override = this.getShellVisualOverrideForRow(row, category);
      if (!override || !override.image) return null;
      return override;
    }

    getShellVisualOverrideForRow(row, category) {
      if (!row) return null;
      const legacy = this.config.shellImageBlocks.byCatalogId.get(row.catalogId) ||
        (row.imageKey ? this.config.shellImageBlocks.byImageKey.get(row.imageKey) : null);
      const exactOption = this.getOptionOverride(row.catalogId, ["catalog", "any"]) ||
        (row.imageKey ? this.getOptionOverride(row.imageKey, ["image", "any"]) : null);
      const drumTypeOption = this.getOptionOverride(row.drumType, ["category", "any"]);
      const fieldOption = category ? this.config.optionBlocks.list.find(candidate => {
        if (!this.optionTargetsCategory(candidate, category.key)) return false;
        if (candidate.matchField) return String(row[candidate.matchField] || "") === candidate.key;
        return Object.values(row).some(value =>
          (typeof value === "string" || typeof value === "number") && String(value) === candidate.key
        );
      }) : null;
      const option = exactOption || fieldOption || drumTypeOption;

      if (option) {
        return {
          image: option.thumbnailImage || option.previewImage || legacy?.image || "",
          previewImage: option.previewImage || option.thumbnailImage || legacy?.image || "",
          image2: option.previewImage2 || option.backImage || legacy?.image2 || "",
          altText: option.label || legacy?.altText || row.sizeLabel
        };
      }

      if (legacy) {
        return {
          image: legacy.image || "",
          previewImage: legacy.image || "",
          image2: legacy.image2 || "",
          altText: legacy.altText || row.sizeLabel
        };
      }

      return null;
    }

    applyCatalogRowOverrides(row) {
      const override = this.getRowDataOverride(row);
      if (!override) return { ...row };
      return {
        ...row,
        displayLabel: override.displayLabel || "",
        descriptionOverride: override.description || "",
        price: override.priceOverride == null ? row.price : override.priceOverride,
        sortOrder: override.sortOrder == null || override.sortOrder === 0 ? row.sortOrder : override.sortOrder
      };
    }

    getRowDataOverride(row) {
      if (!row) return null;
      const legacy = this.config.shellImageBlocks.byCatalogId.get(row.catalogId) ||
        (row.imageKey ? this.config.shellImageBlocks.byImageKey.get(row.imageKey) : null);
      const exactOption = this.getOptionOverride(row.catalogId, ["catalog", "any"]) ||
        (row.imageKey ? this.getOptionOverride(row.imageKey, ["image", "any"]) : null);
      const finishOption = this.getOptionOverride(row.finishKey, ["finish", "any"]);
      const drumTypeOption = this.getOptionOverride(row.drumType, ["category", "any"]);
      const priceOption = exactOption || finishOption || drumTypeOption;
      const sortOption = exactOption || drumTypeOption;

      if (!legacy && !exactOption && !finishOption && !drumTypeOption) return null;
      return {
        displayLabel: exactOption?.label || legacy?.displayLabel || "",
        description: exactOption?.description || legacy?.description || "",
        priceOverride: priceOption?.priceOverride ?? legacy?.priceOverride ?? null,
        sortOrder: sortOption?.sortOrder ?? legacy?.sortOrder ?? null
      };
    }

    getOptionOverride(key, scopes = ["any"]) {
      if (!key) return null;
      const lookupScopes = Array.isArray(scopes) ? scopes : [scopes];
      for (const scope of lookupScopes) {
        const match = this.config.optionBlocks.byScope.get(scope)?.get(key);
        if (match) return match;
      }
      return null;
    }

    selectedShellItems() {
      return this.getCatalogSelectCategories().flatMap(category => {
        return this.getCategoryCatalogSelection(category.key).map(item => {
          const row = this.catalogById(item.catalogId);
          if (row) return { ...item, categoryKey: category.key, row, unavailable: false };
          return { ...item, categoryKey: category.key, row: null, unavailable: true };
        });
      });
    }

    selectedShellTotal(categoryKey) {
      if (categoryKey) {
        return this.getCategoryCatalogSelection(categoryKey).reduce((sum, item) => {
          const row = this.catalogById(item.catalogId);
          return sum + (row ? row.price * item.quantity : 0);
        }, 0);
      }
      return this.getCatalogSelectCategories().reduce((sum, category) => sum + this.selectedShellTotal(category.key), 0);
    }

    addOnTotal() {
      return this.addOns().filter(item => this.state.addOns.has(item.id)).reduce((sum, item) => sum + item.price, 0);
    }

    estimatedTotal() {
      return this.selectedShellTotal() + this.addOnTotal();
    }

    preserveCatalogSelectionsAfterUpstreamChange() {
      let changedCount = 0;
      this.getCatalogSelectCategories().forEach(category => {
        const preserved = [];
        this.getCategoryCatalogSelection(category.key).forEach(item => {
          const oldRow = this.catalogById(item.catalogId);
          const drumType = oldRow?.drumType || item.drumType;
          const sizeKey = oldRow?.sizeKey || item.sizeKey;
          const nextRow = this.findCatalogRow({ categoryKey: category.key, drumType, sizeKey });
          if (nextRow) {
            if (nextRow.catalogId !== item.catalogId) changedCount += item.quantity;
            preserved.push({ catalogId: nextRow.catalogId, quantity: item.quantity, unavailable: false });
            return;
          }
          preserved.push({
            catalogId: this.unavailableId({ drumType, sizeKey }),
            drumType,
            sizeKey,
            quantity: item.quantity,
            unavailable: true
          });
          changedCount += item.quantity;
        });
        this.state.catalogSelections[category.key] = preserved;
      });
      this.state.selectionChangeNotice = changedCount
        ? `${changedCount} selected ${changedCount === 1 ? "item was" : "items were"} updated to match your new options. Please review your current selections.`
        : "";
    }

    buildSummaryText() {
      const pad = (label, value) => {
        const key = String(label || "").padEnd(16, " ");
        return `   ${key}${value}`;
      };

      const lines = [
        "CUSTOM BUILD REQUEST",
        "====================",
        ""
      ];

      if (this.state.quoteNumber) {
        lines.push(`Ref #:         ${this.state.quoteNumber}`);
        lines.push("");
      }

      lines.push("CONFIGURATION");
      lines.push("-------------");
      lines.push(`Series:        ${this.getSeriesLabel()}`);

      this.getCategories().forEach(category => {
        if (category.dataSource === "catalog_field" || category.dataSource === "manual") {
          const label = `${String(category.label || "Option")}:`.padEnd(16, " ");
          lines.push(`${label}${this.categorySelectionSummary(category.key)}`);
        }
      });

      this.getCatalogSelectCategories().forEach(category => {
        const sectionTitle = this.isSnareCategory(category) ? "SNARE OPTIONS" : String(category.label || "SHELLS").toUpperCase();
        lines.push("");
        lines.push(sectionTitle);
        lines.push("-".repeat(sectionTitle.length));
        lines.push("");

        const items = this.selectedShellItems().filter(item => item.categoryKey === category.key);
        if (!items.length) {
          lines.push("   None selected");
          return;
        }

        items.forEach((item, index) => {
          if (index > 0) {
            lines.push("");
            lines.push("   ---------------------------------------");
            lines.push("");
          }

          if (!item.row) {
            lines.push(`${index + 1}. UNAVAILABLE ITEM`);
            lines.push(pad("Type:", `${item.drumType || "unknown"} ${item.sizeKey || ""}`.trim()));
            lines.push(pad("Qty:", item.quantity));
            return;
          }

          const title = [
            item.row.drumTypeLabel || titleFromKey(item.row.drumType),
            item.row.sizeLabel
          ].filter(Boolean).join(" - ");
          const priceLine = item.quantity > 1 ? `${quoteMoney(item.row.price)} each` : quoteMoney(item.row.price);
          const modelNumber = String(item.row.modelNumber || "").trim();
          const stockNumber = String(item.row.stockNumber || "").trim();

          lines.push(`${index + 1}. ${title}`);
          lines.push(pad("Finish:", item.row.finishName || titleFromKey(item.row.finishKey)));
          if (modelNumber) lines.push(pad("Model Number:", modelNumber));
          if (stockNumber) lines.push(pad("SKU:", stockNumber));
          lines.push(pad("Qty:", item.quantity));
          lines.push(pad("Price:", priceLine));
        });
      });

      lines.push("");
      lines.push("ADD-ONS");
      lines.push("------");
      lines.push("");
      const addOns = this.selectedAddOns();
      if (addOns.length) {
        addOns.forEach((addOn, index) => {
          if (index > 0) lines.push("");
          const modelNumber = String(addOn.modelNumber || "").trim();
          const stockNumber = String(addOn.stockNumber || "").trim();
          lines.push(`${index + 1}. ${addOn.name}`);
          lines.push(pad("Price:", quoteMoney(addOn.price)));
          if (modelNumber) lines.push(pad("Model Number:", modelNumber));
          if (stockNumber) lines.push(pad("SKU:", stockNumber));
        });
      } else {
        lines.push("   None selected");
      }

      lines.push("");
      lines.push("TOTALS");
      lines.push("------");
      lines.push(`Estimated Total:     ${quoteMoney(this.estimatedTotal())}`);
      lines.push(`Min. Deposit (25%):  ${quoteMoney(this.estimatedTotal() * 0.25)}`);
      lines.push("Note: Deposit is not required when financing this build.");
      lines.push("");
      lines.push("SHARE LINK");
      lines.push("----------");
      lines.push(this.cleanShareUrl());

      return lines.join("\n");
    }

    getAppMount() {
      let app = this.root.querySelector("[data-ddkb-app]");
      if (app) return app;

      // Back-compat when Liquid has not been updated yet: preserve contact host + config.
      const keep = Array.from(this.root.children).filter(node =>
        node.matches?.("[data-ddkb-config], [data-ddkb-contact-host], [data-ddkb-app]")
      );
      app = document.createElement("div");
      app.setAttribute("data-ddkb-app", "");
      this.root.replaceChildren(...keep, app);
      return app;
    }

    getShopifyContactForm() {
      return this.root.querySelector("[data-ddkb-contact-host] form") || null;
    }

    mountShopifyContactForm() {
      const contactHost = this.root.querySelector("[data-ddkb-contact-host]");
      const shopifyForm = this.getShopifyContactForm();
      const renderedForm = this.getAppMount().querySelector("[data-quote-form]");
      if (!shopifyForm || !renderedForm || shopifyForm === renderedForm) return;

      renderedForm.querySelectorAll('input[name="form_type"], input[name="utf8"]').forEach(input => {
        input.remove();
      });

      shopifyForm.className = renderedForm.className;
      shopifyForm.setAttribute("data-quote-form", "");
      shopifyForm.setAttribute("data-shopify-captcha", "true");
      shopifyForm.append(...Array.from(renderedForm.childNodes));
      renderedForm.replaceWith(shopifyForm);
      contactHost?.remove();
    }

    cleanShareUrl() {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("contact_posted");
        url.searchParams.delete(QUOTE_RETURN_PARAM);
        url.searchParams.delete("form_type");
        if (!this.getCategories().some(category => category.key === "series")) url.searchParams.delete("series");
        return url.toString();
      } catch (_) {
        return window.location.href;
      }
    }

    quoteConfirmationStorageKey() {
      return `${QUOTE_CONFIRMATION_STORAGE_PREFIX}${this.root.id || "builder"}`;
    }

    buildContactReturnPath() {
      try {
        const url = new URL(this.cleanShareUrl());
        url.searchParams.set(QUOTE_RETURN_PARAM, "1");
        return `${url.pathname}${url.search}${url.hash}`;
      } catch (_) {
        return `${window.location.pathname}?${QUOTE_RETURN_PARAM}=1`;
      }
    }

    persistQuoteConfirmationSnapshot() {
      const snapshot = {
        quoteNumber: this.state.quoteNumber || "",
        firstName: this.confirmationFirstName(),
        price: quoteMoney(this.estimatedTotal()),
        build: this.confirmationBuildSummary(),
        shareUrl: this.cleanShareUrl(),
        supportEmail: this.config.supportEmail || "",
        supportPhone: this.config.supportPhone || "",
        submittedAt: Date.now()
      };
      this.confirmationSnapshot = snapshot;
      try {
        window.sessionStorage.setItem(this.quoteConfirmationStorageKey(), JSON.stringify(snapshot));
      } catch (error) {
        this.warn("could not persist quote confirmation snapshot", error);
      }
      return snapshot;
    }

    readQuoteConfirmationSnapshot() {
      if (this.confirmationSnapshot) return this.confirmationSnapshot;
      try {
        const raw = window.sessionStorage.getItem(this.quoteConfirmationStorageKey());
        const parsed = safeJson(raw, null);
        if (parsed && typeof parsed === "object") {
          this.confirmationSnapshot = parsed;
          return parsed;
        }
      } catch (_) {
        /* ignore storage failures */
      }
      return null;
    }

    stripQuoteReturnParamsFromUrl() {
      try {
        const url = new URL(window.location.href);
        let changed = false;
        ["contact_posted", QUOTE_RETURN_PARAM, "form_type"].forEach(key => {
          if (url.searchParams.has(key)) {
            url.searchParams.delete(key);
            changed = true;
          }
        });
        if (!changed) return;
        window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      } catch (_) {
        /* ignore */
      }
    }

    hasQuoteReturnSignal() {
      try {
        const params = new URLSearchParams(window.location.search);
        const posted = params.get("contact_posted");
        return params.get(QUOTE_RETURN_PARAM) === "1" || posted === "true" || posted === "1";
      } catch (_) {
        return false;
      }
    }

    maybeRestoreQuoteConfirmation() {
      if (!this.hasQuoteReturnSignal()) return;
      const snapshot = this.readQuoteConfirmationSnapshot();
      if (snapshot?.quoteNumber) this.state.quoteNumber = snapshot.quoteNumber;
      this.state.quoteSubmitted = true;
      this.stripQuoteReturnParamsFromUrl();
      window.requestAnimationFrame(() => {
        this.showQuoteConfirmation();
      });
    }

    shouldSimulateContactSubmit() {
      // Local preview / non-Shopify hosts have no real /contact endpoint.
      if (window.Shopify?.shop || window.Shopify?.routes) return false;
      const host = window.location.hostname;
      return host === "localhost" || host === "127.0.0.1" || window.location.protocol === "file:";
    }

    setQuoteFormStatus(message) {
      const status = this.elements.quoteStatus;
      if (!status) return;
      if (!message) {
        status.hidden = true;
        status.textContent = "";
        return;
      }
      status.hidden = false;
      status.textContent = message;
    }

    findCatalogRow({ finishKey, drumType, sizeKey, categoryKey } = {}) {
      const currentCategory = categoryKey ? this.getCategory(categoryKey) : null;
      const rows = this.scopeRowsForCategory(
        currentCategory,
        this.catalog.filter(row => row.series === this.state.series)
      );
      return rows.find(row => {
        if (drumType && row.drumType !== drumType) return false;
        if (sizeKey && row.sizeKey !== sizeKey) return false;
        if (finishKey && row.finishKey !== finishKey) return false;
        for (const category of this.getCategories()) {
          if (category.dataSource !== "catalog_field") continue;
          if (this.shouldIgnoreUpstreamCategory(currentCategory, category)) continue;
          const field = this.catalogFieldForCategory(category);
          const value = this.getResolvedCategorySelection(category);
          if (!value) continue;
          const values = Array.isArray(value) ? value : [value];
          if (!values.includes(row[field])) return false;
        }
        return true;
      }) || null;
    }

    validateBlockCatalogCoverage() {
      this.getCategories().forEach(category => {
        if (category.dataSource !== "catalog_field") return;
        const field = this.catalogFieldForCategory(category);
        if (!field) return;
        const values = new Set(this.catalogRowsBeforeCategory(category.key).map(row => row[field]));
        this.config.optionBlocks.list
          .filter(option => this.optionTargetsCategory(option, category.key))
          .forEach(option => {
            if (!values.has(option.key)) this.warn("option exists but has no matching catalog rows", { category: category.key, option: option.key });
          });
      });

      this.config.shellImageBlocks.list.forEach(block => {
        const catalogIdMatch = block.catalogId && this.catalogMap.has(block.catalogId);
        const imageKeyMatch = block.imageKey && this.catalog.some(row => row.imageKey === block.imageKey);
        if (!catalogIdMatch && !imageKeyMatch) this.warn("shell override block exists but has no matching catalog row", block.catalogId || block.imageKey);
      });
    }

    ensureValidSelections() {
      if (!this.state.selections) this.state.selections = {};
      if (!this.state.catalogSelections) this.state.catalogSelections = {};
      this.syncActiveSeries();

      this.getCategories().forEach(category => {
        if (category.dataSource === "catalog_field" || category.dataSource === "manual") {
          const options = this.getOptionsForCategory(category.key);
          if (!options.length) {
            if (this.catalogLoaded && !String(this.config.defaultSeriesKey || "").trim()) {
              this.state.selections[category.key] = category.multiSelect ? [] : null;
              if (this.catalogFieldForCategory(category) === "series") this.syncActiveSeries();
            }
            return;
          }
          const current = this.getCategorySelection(category.key);
          const hasCurrent = current != null && current !== "" && !(Array.isArray(current) && !current.length);
          if (hasCurrent) {
            const isValid = category.multiSelect
              ? Array.isArray(current) && current.every(value => options.some(option => option.key === value))
              : options.some(option => option.key === current);
            if (isValid) return;
            this.state.selections[category.key] = category.multiSelect ? [] : null;
            if (this.catalogFieldForCategory(category) === "series") this.syncActiveSeries();
          }

          // Theme option: when only one choice remains, select it automatically.
          if (category.autoSelectSingleOption && options.length === 1) {
            const onlyKey = options[0].key;
            const selected = this.getCategorySelection(category.key);
            const alreadySelected = category.multiSelect
              ? Array.isArray(selected) && selected.includes(onlyKey)
              : selected === onlyKey;
            if (!alreadySelected) {
              this.state.selections[category.key] = category.multiSelect ? [onlyKey] : onlyKey;
            }
          }
          if (this.catalogFieldForCategory(category) === "series") this.syncActiveSeries();
          return;
        }

        if (category.dataSource === "catalog_select" && !this.state.catalogSelections[category.key]) {
          this.state.catalogSelections[category.key] = [];
        }
      });

      if (this.catalogLoaded) this.pruneCatalogSelectionsToCategoryScope();

      // Auto-select a lone catalog item after scope prune (shells/snares).
      if (this.catalogLoaded) {
        this.getCategories().forEach(category => {
          if (category.dataSource !== "catalog_select" || !category.autoSelectSingleOption) return;
          if (!this.priorCategoriesReady(category.key)) return;
          const rows = this.catalogRowsBeforeCategory(category.key, { requirePriorSelections: true });
          if (rows.length !== 1) return;
          const onlyId = rows[0].catalogId;
          const current = this.getCategoryCatalogSelection(category.key);
          if (current.some(item => item.catalogId === onlyId)) return;
          if (current.length) return;
          this.state.catalogSelections[category.key] = [{
            catalogId: onlyId,
            quantity: 1,
            unavailable: false
          }];
        });
      }

      const stepOrder = this.getStepOrder();
      if (!stepOrder.includes(this.state.activeStep)) {
        this.state.activeStep = stepOrder[0] || "";
      }
    }

    selectCategoryOption(categoryKey, value) {
      const category = this.getCategory(categoryKey);
      if (!category || !value) return;
      if (!this.getOptionsForCategory(categoryKey).some(option => option.key === value)) return;
      const previousSeries = this.state.series;

      let clearedSelection = false;

      if (category.multiSelect) {
        let current = this.getCategorySelection(categoryKey);
        if (!Array.isArray(current)) current = current ? [current] : [];
        const next = current.includes(value)
          ? current.filter(item => item !== value)
          : [...current, value];
        clearedSelection = !next.length;
        this.state.selections[categoryKey] = next.length ? next : null;
      } else {
        const current = this.getCategorySelection(categoryKey);
        const next = current === value ? null : value;
        clearedSelection = !next;
        this.state.selections[categoryKey] = next;
      }

      this.syncActiveSeries();
      if (previousSeries !== this.state.series) {
        this.state.catalogSelections = {};
        this.state.categoryFilters = {};
        this.state.selectionChangeNotice = "";
        this.state.photoIndex = 0;
      }
      this.ensureValidSelections();
      this.preserveCatalogSelectionsAfterUpstreamChange();
      // Re-run so single-option auto-select can apply after upstream remap/pruning.
      this.ensureValidSelections();
      if (clearedSelection) this.state.photoIndex = 0;
      this.state.lastDisplayedPhoto = null;
      this.update();
    }

    toggleCatalogItem(categoryKey, catalogId) {
      const row = this.catalogById(catalogId);
      if (!row) return;
      if (!this.state.catalogSelections[categoryKey]) this.state.catalogSelections[categoryKey] = [];
      const items = this.state.catalogSelections[categoryKey];
      const index = items.findIndex(item => item.catalogId === catalogId);
      if (index >= 0) items.splice(index, 1);
      else items.push({ catalogId, quantity: 1, unavailable: false });
      this.update();
    }

    adjustCatalogItemQuantity(categoryKey, catalogId, delta) {
      const items = this.state.catalogSelections[categoryKey] || [];
      const item = items.find(shell => shell.catalogId === catalogId);
      if (!item) return;
      item.quantity = Math.max(1, item.quantity + (Number(delta) || 0));
      this.update();
    }

    removeUnavailableCatalogItem(categoryKey, catalogId) {
      this.state.catalogSelections[categoryKey] = (this.state.catalogSelections[categoryKey] || [])
        .filter(item => item.catalogId !== catalogId);
      this.update();
    }

    setActiveStep(step) {
      if (!this.getStepOrder().includes(step)) return;
      const stepChanged = this.state.activeStep !== step;
      if (stepChanged) {
        this.state.activeAddonFilter = "";
        // Clear add-on stage preview when leaving the add-ons step.
        if (this.getCategory(step)?.dataSource !== "addons") {
          this.state.activeAddonPreview = null;
        }
      }
      this.state.activeStep = step;
      this.update();
      if (stepChanged) this.settleMobileStepTransition();
    }

    goToNextStep() {
      const stepOrder = this.getStepOrder();
      const currentIndex = stepOrder.indexOf(this.state.activeStep);
      this.setActiveStep(stepOrder[Math.min(stepOrder.length - 1, currentIndex + 1)] || "");
    }

    updatePhotoTriggerAccessibility() {
      if (!this.elements.photo) return;
      const isMobile = window.matchMedia?.(MOBILE_LAYOUT_QUERY).matches;
      if (isMobile) {
        this.elements.photo.setAttribute("role", "button");
        this.elements.photo.setAttribute("tabindex", "0");
        this.elements.photo.setAttribute("aria-label", "Open enlarged kit photo");
        return;
      }
      this.elements.photo.removeAttribute("role");
      this.elements.photo.removeAttribute("tabindex");
      this.elements.photo.removeAttribute("aria-label");
      this.closePhotoViewer();
    }

    openPhotoViewer() {
      if (!window.matchMedia?.(MOBILE_LAYOUT_QUERY).matches) return;
      if (!this.elements.photoViewer || !this.photos().some(photo => photo?.src)) return;
      this.updatePhotoViewer();
      if (!this.elements.photoViewer.open) this.elements.photoViewer.showModal();
    }

    closePhotoViewer() {
      if (this.elements.photoViewer?.open) this.elements.photoViewer.close();
    }

    updatePhotoViewer(photos = this.photos()) {
      if (!this.elements.photoViewerImage) return;
      const photo = photos[this.state.photoIndex] || photos[0];
      if (!photo?.src) return;
      this.elements.photoViewerImage.src = photo.src;
      this.elements.photoViewerImage.alt = photo.alt || "";
      this.elements.photoViewerLabel.textContent = photo.label || photo.alt || "Kit photo";
      this.elements.photoViewerCount.textContent = photos.length > 1
        ? `${this.state.photoIndex + 1} / ${photos.length}`
        : "";
      this.elements.photoViewerPrev.hidden = this.state.photoIndex <= 0 || photos.length <= 1;
      this.elements.photoViewerNext.hidden = this.state.photoIndex >= photos.length - 1 || photos.length <= 1;
    }

    changePhoto(direction) {
      const photos = this.photos();
      const nextIndex = this.state.photoIndex + direction;
      if (nextIndex < 0 || nextIndex >= photos.length) return;
      this.state.photoIndex = nextIndex;
      this.updateSummary();
      this.updateUrl();
    }

    toggleRail() {
      this.state.railCollapsed = !this.state.railCollapsed;
      this.update();
    }

    toggleMobileQuoteBar() {
      this.state.mobileQuoteExpanded = !this.state.mobileQuoteExpanded;
      const quoteBar = this.root.querySelector("[data-quote-bar]");
      const toggle = this.root.querySelector("[data-toggle-mobile-quote]");
      quoteBar?.classList.toggle("is-mobile-expanded", this.state.mobileQuoteExpanded);
      if (toggle) {
        toggle.textContent = String.fromCharCode(this.state.mobileQuoteExpanded ? 8964 : 8963);
        toggle.setAttribute("aria-expanded", this.state.mobileQuoteExpanded ? "true" : "false");
        toggle.setAttribute("aria-label", this.state.mobileQuoteExpanded ? "Collapse build summary" : "Expand build summary");
      }
    }

    toggleMobilePreview() {
      if (!window.matchMedia?.(MOBILE_LAYOUT_QUERY).matches) return;
      this.state.mobilePreviewHidden = !this.state.mobilePreviewHidden;
      this.syncMobilePreviewVisibility();
    }

    syncMobilePreviewVisibility() {
      const builder = this.elements.builder;
      const toggle = this.root.querySelector("[data-toggle-mobile-preview]");
      const isMobile = window.matchMedia?.(MOBILE_LAYOUT_QUERY).matches;
      const activeCategory = this.getCategory(this.state.activeStep);
      // Hide/show image control is only for configuration steps â€” not rewards or review.
      const allowPreviewToggle = isMobile &&
        activeCategory?.dataSource !== "savings" &&
        activeCategory?.dataSource !== "review";
      const hidden = allowPreviewToggle && !!this.state.mobilePreviewHidden;
      builder?.classList.toggle("is-preview-hidden", hidden);
      if (!toggle) return;
      toggle.hidden = !allowPreviewToggle;
      toggle.setAttribute("aria-pressed", hidden ? "true" : "false");
      toggle.setAttribute("aria-label", hidden ? "Show kit image" : "Hide kit image");
      toggle.textContent = hidden ? "Show image" : "Hide image";
    }

    toggleAddonRow(rowKey) {
      if (!rowKey) return;
      if (this.state.expandedAddonRows.has(rowKey)) this.state.expandedAddonRows.delete(rowKey);
      else this.state.expandedAddonRows.add(rowKey);
      this.update();
    }

    toggleAddOn(id) {
      if (this.state.addOns.has(id)) this.state.addOns.delete(id);
      else this.state.addOns.add(id);
      const addOn = this.addOns().find(item => item.id === id);
      if (addOn) this.state.activeAddonFilter = this.addOnFilterKey(addOn);
      this.update();
    }

    async buyAddOn(id) {
      const addOn = this.addOns().find(item => item.id === id);
      if (!addOn) return;
      const buyButton = this.root.querySelector(`[data-buy-now="${CSS.escape(id)}"]`);

      if (addOn.available === false) {
        if (buyButton) {
          buyButton.textContent = "Unavailable";
          buyButton.disabled = true;
        } else if (addOn.productUrl) {
          window.location.href = addOn.productUrl;
        }
        return;
      }

      if (buyButton) {
        buyButton.disabled = true;
        buyButton.textContent = "Adding...";
      }

      if (addOn.variantId) {
        try {
          const response = await fetch(CART_ADD_ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: addOn.variantId, quantity: 1 })
          });
          if (!response.ok) throw new Error("Cart add failed");
          if (buyButton) {
            buyButton.textContent = "Added to cart";
            buyButton.classList.add("is-added");
          }
          return;
        } catch (error) {
          if (buyButton) {
            buyButton.disabled = false;
            buyButton.textContent = "Buy add-on";
          }
          return;
        }
      }

      if (buyButton) {
        buyButton.disabled = false;
        buyButton.textContent = "Buy add-on";
      }
    }

    update() {
      if (window.matchMedia?.(MOBILE_LAYOUT_QUERY).matches) {
        this.state.railCollapsed = true;
      }
      // Re-rendering panels rebuilds horizontal scrollers â€” keep the user's place.
      const scrollState = this.capturePanelScrollState();
      this.ensureValidSelections();
      this.syncPhotoIndexToAvailableViews();
      this.renderPanels();
      this.renderStepRail();
      this.elements.builder?.classList.toggle("sdkc-rail-collapsed", !!this.state.railCollapsed);
      this.syncMobileTextStepClass();
      this.root.querySelector("[data-quote-bar]")?.classList.toggle("is-mobile-expanded", !!this.state.mobileQuoteExpanded);
      this.updateSummary();
      this.updateUrl();
      this.syncModuleHeights();
      this.syncMobileScrollHints();
      this.restorePanelScrollState(scrollState);
      // Second pass after layout so scrollLeft sticks once widths are known.
      window.requestAnimationFrame(() => {
        this.restorePanelScrollState(scrollState);
        this.syncMobileScrollHints();
      });
    }

    panelScrollSelectors() {
      return [
        ".sdkc-filter-bar",
        ".ddkb-shell-grid",
        ".sdkc-swatch-grid",
        ".sdkc-option-grid",
        ".sdkc-addon-product-grid",
        ".sdkc-stage-toolbar .sdkc-drum-lineup-track"
      ];
    }

    capturePanelScrollState() {
      const scrollers = [];
      this.panelScrollSelectors().forEach(sel => {
        this.root.querySelectorAll(sel).forEach((el, index) => {
          if (!el.scrollLeft && !el.scrollTop) return;
          scrollers.push({
            sel,
            index,
            left: el.scrollLeft,
            top: el.scrollTop
          });
        });
      });
      return {
        windowX: window.scrollX,
        windowY: window.scrollY,
        scrollers
      };
    }

    restorePanelScrollState(state) {
      if (!state) return;
      state.scrollers.forEach(({ sel, index, left, top }) => {
        const el = this.root.querySelectorAll(sel)[index];
        if (!el) return;
        if (typeof left === "number") el.scrollLeft = left;
        if (typeof top === "number") el.scrollTop = top;
      });
      if (typeof state.windowX === "number" && typeof state.windowY === "number") {
        window.scrollTo(state.windowX, state.windowY);
      }
    }

    syncMobileTextStepClass() {
      const category = this.getCategory(this.state.activeStep);
      const mobile = this.isMobileLayout();
      const isTextStep = mobile
        && (category?.dataSource === "savings" || category?.dataSource === "review");
      const isAddonsStep = mobile && category?.dataSource === "addons";
      this.elements.builder?.classList.toggle("is-text-step", !!isTextStep);
      this.elements.builder?.classList.toggle("is-addons-step", !!isAddonsStep);
    }

    syncMobileScrollHints() {
      if (!this.isMobileLayout()) {
        this.root.querySelectorAll(".sdkc-scroll-hint").forEach(wrap => {
          const scroller = wrap.querySelector(".sdkc-scroll-hint-target") || wrap.firstElementChild;
          if (scroller && wrap.parentNode) wrap.parentNode.insertBefore(scroller, wrap);
          wrap.remove();
        });
        return;
      }

      const selectors = [
        ".ddkb-shell-grid",
        ".sdkc-swatch-grid",
        ".sdkc-option-grid",
        ".sdkc-addon-product-grid",
        ".sdkc-filter-bar",
        ".sdkc-stage-toolbar .sdkc-drum-lineup-track"
      ].join(", ");

      this.root.querySelectorAll(selectors).forEach(scroller => {
        if (scroller.closest(".sdkc-scroll-hint")) return;
        const wrap = document.createElement("div");
        wrap.className = "sdkc-scroll-hint";
        scroller.classList.add("sdkc-scroll-hint-target");
        scroller.parentNode?.insertBefore(wrap, scroller);
        wrap.appendChild(scroller);

        const arrow = document.createElement("span");
        arrow.className = "sdkc-scroll-hint-arrow";
        arrow.setAttribute("aria-hidden", "true");
        arrow.textContent = String.fromCharCode(8250);
        wrap.appendChild(arrow);

        scroller.addEventListener("scroll", () => this.updateScrollHint(wrap, scroller), { passive: true });
      });

      window.requestAnimationFrame(() => {
        this.root.querySelectorAll(".sdkc-scroll-hint").forEach(wrap => {
          const scroller = wrap.querySelector(".sdkc-scroll-hint-target");
          this.updateScrollHint(wrap, scroller);
        });
      });
    }

    updateScrollHint(wrap, scroller) {
      if (!wrap || !scroller) return;
      const canScroll = scroller.scrollWidth > scroller.clientWidth + 6;
      const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 6;
      wrap.classList.toggle("has-more-end", canScroll && !atEnd);
    }

    renderPanels() {
      const category = this.getCategory(this.state.activeStep);
      const body = category
        ? this.categoryStepMarkup(category)
        : `<p>Add Category blocks in the theme editor to create numbered build steps.</p>`;
      this.elements.panels.innerHTML = `<article class="sdkc-panel" data-panel="${escapeHtml(this.state.activeStep)}"><div class="sdkc-panel-body">${body}</div></article>`;
    }

    renderStepRail() {
      const steps = this.getCategories();
      this.elements.stepRail.style.setProperty("--sdkc-step-count", Math.max(1, steps.length));
      this.elements.stepRail.innerHTML = steps.map(category => `
        <button type="button" class="sdkc-rail-step ${this.state.activeStep === category.key ? "is-active" : ""}" data-panel-toggle="${escapeHtml(category.key)}">
          <span>${String(category.stepNumber || category.sortOrder).padStart(2, "0")}</span>
          <strong>${escapeHtml(category.label)}</strong>
          <small>${category.dataSource === "review"
            ? priceWithTaxMarkup(this.estimatedTotal())
            : this.stepSummary(category.key)}</small>
        </button>
      `).join("");
      this.centerActiveMobileStep();
    }

    centerActiveMobileStep() {
      if (!window.matchMedia?.(MOBILE_LAYOUT_QUERY).matches) return;
      const rail = this.elements.stepRail;
      const activeStep = rail?.querySelector(".sdkc-rail-step.is-active");
      if (!rail || !activeStep) return;
      window.requestAnimationFrame(() => {
        const targetLeft = activeStep.offsetLeft - ((rail.clientWidth - activeStep.offsetWidth) / 2);
        rail.scrollTo({ left: Math.max(0, targetLeft), behavior: "auto" });
      });
    }

    settleMobileStepTransition() {
      if (!window.matchMedia?.(MOBILE_LAYOUT_QUERY).matches) return;
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.updateStickyOffset();
          const builder = this.elements.builder;
          if (!builder) return;
          const builderTop = window.scrollY + builder.getBoundingClientRect().top;
          window.scrollTo({ top: Math.max(0, Math.round(builderTop)), left: 0, behavior: "auto" });
        });
      });
    }

    categoryStepMarkup(category) {
      if (category.dataSource === "addons") return this.addOnsMarkup(category);
      if (category.dataSource === "savings") return this.savingsMarkup();
      if (category.dataSource === "review") return this.reviewMarkup();
      if (category.dataSource === "catalog_select") return this.catalogSelectMarkup(category);
      return this.catalogFieldMarkup(category);
    }

    catalogFieldMarkup(category) {
      if (this.loadingOrErrorMarkup(category)) return this.loadingOrErrorMarkup(category);
      if (!this.priorCategoriesReady(category.key)) {
        return `<p>Complete the previous steps before choosing ${escapeHtml(category.label.toLowerCase())}.</p>${this.nextButton()}`;
      }

      const options = this.getOptionsForCategory(category.key);
      if (!options.length) {
        return `<p>No options were found for ${escapeHtml(category.label)}. Check the catalog field "${escapeHtml(category.catalogField || category.key)}" or add Option blocks.</p>${this.nextButton()}`;
      }

      const rawGroupNames = category.groupField
        ? [...new Set(options.map(option => option.group).filter(Boolean))]
        : [];
      const catalogRows = category.groupField
        ? this.catalogRowsBeforeCategory(category.key)
        : [];
      const groupNames = category.groupField
        ? this.sortCategoryFilterLabels(category, rawGroupNames, {
          rows: catalogRows,
          groupField: category.groupField
        })
        : [];
      this.pruneStaleCategoryFilter(category.key, groupNames);
      const activeFilter = this.resolveCategoryFilter(category.key, groupNames);
      const filterButtons = this.categoryFilterButtons(category.key, groupNames, activeFilter);
      const visibleOptions = activeFilter
        ? options.filter(option => option.group === activeFilter)
        : options;
      const groupedOptions = category.groupField
        ? groupBy(visibleOptions, option => option.group || category.label)
        : { [category.label]: visibleOptions };
      const groups = category.groupField
        ? Object.fromEntries(groupNames.map(name => [name, groupedOptions[name] || []]).filter(([, items]) => items.length))
        : groupedOptions;
      const hasThumbs = visibleOptions.some(option => option.thumbnailImage || option.swatchImage);

      const optionButtons = (items) => items.map(option => {
        const selected = this.isCategoryOptionSelected(category, option.key);
        const thumb = option.thumbnailImage || option.swatchImage || "";
        if (hasThumbs) {
          return `
            <button type="button" class="sdkc-category-option sdkc-swatch ${selected ? "is-selected" : ""} ${thumb ? "has-thumb" : ""}" data-category-key="${escapeHtml(category.key)}" data-category-option="${escapeHtml(option.key)}">
              <span class="sdkc-swatch-thumb">${thumb ? `<img src="${escapeHtml(thumb)}" alt="" loading="lazy">` : ""}</span>
              <strong>${escapeHtml(option.label || option.name)}</strong>
              ${this.optionCardCopyMarkup(category, option, { swatch: true })}
            </button>
          `;
        }
        return `
          <button type="button" class="sdkc-category-option sdkc-choice ${selected ? "is-selected" : ""}" data-category-key="${escapeHtml(category.key)}" data-category-option="${escapeHtml(option.key)}">
            <span>${escapeHtml(option.label || option.name)}</span>
            ${this.optionCardCopyMarkup(category, option)}
          </button>
        `;
      }).join("");

      const groupsMarkup = Object.entries(groups).map(([group, items]) => `
        <div class="sdkc-finish-group">
          ${category.groupField ? `<h3>${escapeHtml(group)}</h3>` : ""}
          <div class="sdkc-category-option-grid ${hasThumbs ? "sdkc-swatch-grid" : "sdkc-option-grid"} ${category.stackOptionsVertically ? "is-stacked-vertically" : this.singleOptionLayoutClass(category, items.length)}">${optionButtons(items)}</div>
        </div>
      `).join("");

      return `
        ${category.help ? `<p class="ddkb-estimate-note">${escapeHtml(category.help)}</p>` : ""}
        ${filterButtons}
        ${groupsMarkup}
        ${this.nextButton()}
      `;
    }

    singleOptionLayoutClass(category, itemCount) {
      if (itemCount !== 1 || category.singleOptionLayout === "standard") return "";
      if (category.singleOptionLayout === "automatic" && this.getOptionsForCategory(category.key).length !== 1) return "";
      // Mobile keeps normal card sizing; the expanded single-option layout is desktop-only.
      if (this.isMobileLayout()) return "";
      return "is-single-option";
    }

    shellGroupLabel(row, groupField) {
      if (groupField === "drumType" || groupField === "drumTypeLabel") {
        return row.drumTypeLabel || titleFromKey(row.drumType) || "Items";
      }
      return String(row[groupField] || row.drumTypeLabel || titleFromKey(row.drumType) || "Items");
    }

    shellGroupSortValue(label) {
      const key = normalizeAddonCategoryKey(label);
      if (key.includes("bass") || key.includes("kick")) return 10;
      if (key.includes("rack") || key === "tom") return 20;
      if (key.includes("floor")) return 30;
      if (key.includes("snare")) return 40;
      return 100;
    }

    catalogSelectMarkup(category) {
      if (this.loadingOrErrorMarkup(category)) return this.loadingOrErrorMarkup(category);
      if (!this.priorCategoriesReady(category.key)) {
        return `<p>Complete the previous steps before choosing ${escapeHtml(category.label.toLowerCase())}.</p>${this.nextButton()}`;
      }

      const rows = this.prioritizeCategoryRows(
        this.catalogRowsBeforeCategory(category.key, { requirePriorSelections: true }),
        category
      );
      const groupField = category.groupField || "drumTypeLabel";
      const grouped = groupBy(rows, row => this.shellGroupLabel(row, groupField));
      const groupNames = this.sortCategoryFilterLabels(category, Object.keys(grouped), {
        rows,
        groupField,
        fallbackSort: (a, b) => this.shellGroupSortValue(a) - this.shellGroupSortValue(b) || naturalCompare(a, b)
      });
      this.pruneStaleCategoryFilter(category.key, groupNames);
      const activeFilter = this.resolveCategoryFilter(category.key, groupNames);
      const visibleGroups = (activeFilter
        ? groupNames.filter(label => label === activeFilter)
        : groupNames
      ).map(label => [label, grouped[label] || []]);
      // Mobile relies on the drum lineup; "Current selections" only appears on Review.
      const selectedSummary = this.isMobileLayout()
        ? ""
        : this.selectedShellsMarkup(category.key);

      const groupMarkup = visibleGroups.map(([label, groupRows]) => `
        <section class="ddkb-shell-group">
          <div class="ddkb-shell-group-head">
            <h3>${escapeHtml(label)}</h3>
          </div>
          ${this.catalogSubgroupMarkup(groupRows, category)}
        </section>
      `).join("");

      const selectedItems = this.getCategoryCatalogSelection(category.key);
      // Desktop keeps the selection stats line; mobile uses the drum lineup instead.
      const selectionStats = this.isMobileLayout()
        ? ""
        : `<div class="ddkb-estimate-note ddkb-shell-selection-stats">${selectedItems.length} item ${selectedItems.length === 1 ? "type" : "types"} selected / ${this.selectedShellQuantity(category.key)} total shells / ${money(this.selectedShellTotal(category.key))}</div>`;
      return `
        ${selectionStats}
        ${this.categoryFilterButtons(category.key, groupNames, activeFilter)}
        ${selectedSummary}
        ${this.selectionChangeNoticeMarkup(category)}
        ${groupMarkup || `<p>No catalog rows match the current selections for ${escapeHtml(category.label)}.</p>`}
        ${this.nextButton()}
      `;
    }

    selectionChangeNoticeMarkup(category) {
      if (!this.state.selectionChangeNotice || category?.showSelectionChangeWarning === false) return "";
      return `
        <div class="ddkb-warning ddkb-selection-change-notice" role="status">
          <span>${escapeHtml(this.state.selectionChangeNotice)}</span>
          <button type="button" data-dismiss-selection-notice aria-label="Dismiss selection change notice">&times;</button>
        </div>
      `;
    }

    prioritizeCategoryRows(rows, category) {
      if (!category?.prioritizeActiveSelection || !category.priorityCategoryKey || !category.priorityRowField) {
        return rows;
      }
      const activeValue = this.getCategorySelection(category.priorityCategoryKey);
      const activeValues = new Set((Array.isArray(activeValue) ? activeValue : [activeValue]).filter(Boolean));
      if (!activeValues.size) return rows;
      return rows
        .map((row, index) => ({ row, index }))
        .sort((a, b) => {
          const aMatches = activeValues.has(a.row[category.priorityRowField]) ? 0 : 1;
          const bMatches = activeValues.has(b.row[category.priorityRowField]) ? 0 : 1;
          return aMatches - bMatches || a.index - b.index;
        })
        .map(item => item.row);
    }

    catalogSubgroupMarkup(rows, category) {
      if (!rows.length) return `<p class="ddkb-estimate-note">No catalog rows match this group.</p>`;
      if (!category.subgroupField) {
        return `<div class="sdkc-category-option-grid ddkb-shell-grid">${rows.map(row => this.shellCardMarkup(row, category)).join("")}</div>`;
      }
      const subgroups = groupBy(rows, row => String(row[category.subgroupField] || "Other"));
      return Object.entries(subgroups).map(([label, subgroupRows]) => `
        <div class="ddkb-shell-subgroup">
          <h4>${escapeHtml(label)}</h4>
          <div class="sdkc-category-option-grid ddkb-shell-grid">${subgroupRows.map(row => this.shellCardMarkup(row, category)).join("")}</div>
        </div>
      `).join("");
    }

    isCategoryOptionSelected(category, optionKey) {
      const current = this.getCategorySelection(category.key);
      if (category.multiSelect) return Array.isArray(current) && current.includes(optionKey);
      return current === optionKey;
    }

    optionCardCopyMarkup(category, option, { swatch = false } = {}) {
      const description = String(option.description || "").trim();
      if (swatch) {
        return description ? `<small class="sdkc-option-description">${escapeHtml(description)}</small>` : "";
      }
      return description ? `<strong>${escapeHtml(description)}</strong>` : "";
    }

    parseFilterOrder(category) {
      const raw = String(category?.filterOrder || "").trim();
      if (!raw) return [];
      return raw.split(",").map(part => part.trim()).filter(Boolean);
    }

    normalizeFilterMatchKey(value) {
      return slugify(String(value || "").trim());
    }

    buildFilterLabelAliases(labels, rows = [], groupField = "") {
      const aliases = new Map();
      const uniqueLabels = [...new Set(labels.filter(Boolean))];

      uniqueLabels.forEach(label => {
        aliases.set(label, label);
        aliases.set(this.normalizeFilterMatchKey(label), label);
        aliases.set(normalizeAddonCategoryKey(label), label);
      });

      rows.forEach(row => {
        const label = groupField
          ? this.shellGroupLabel(row, groupField)
          : "";
        if (!label || !uniqueLabels.includes(label)) return;

        [
          label,
          row[groupField],
          row.drumType,
          row.drumTypeLabel,
          row.finishFamily,
          row.finishFamilyLabel
        ].forEach(alias => {
          if (alias == null || Array.isArray(alias)) return;
          const text = String(alias).trim();
          if (!text) return;
          aliases.set(text, label);
          aliases.set(this.normalizeFilterMatchKey(text), label);
          aliases.set(normalizeAddonCategoryKey(text), label);
        });
      });

      return aliases;
    }

    resolveFilterLabelFromKey(key, labels, aliases = new Map()) {
      const text = String(key || "").trim();
      if (!text) return "";

      const direct = labels.find(label =>
        label === text
        || this.normalizeFilterMatchKey(label) === this.normalizeFilterMatchKey(text)
        || normalizeAddonCategoryKey(label) === normalizeAddonCategoryKey(text)
      );
      if (direct) return direct;

      return aliases.get(text)
        || aliases.get(this.normalizeFilterMatchKey(text))
        || aliases.get(normalizeAddonCategoryKey(text))
        || "";
    }

    sortCategoryFilterLabels(category, labels, { rows = [], groupField = "", fallbackSort } = {}) {
      const uniqueLabels = [...new Set(labels.filter(Boolean))];
      if (!uniqueLabels.length) return [];

      const orderKeys = this.parseFilterOrder(category);
      if (!orderKeys.length) {
        if (typeof fallbackSort === "function") return [...uniqueLabels].sort(fallbackSort);
        return [...uniqueLabels].sort((a, b) => naturalCompare(a, b));
      }

      const aliases = this.buildFilterLabelAliases(uniqueLabels, rows, groupField);
      const ordered = [];
      const used = new Set();

      orderKeys.forEach(key => {
        const match = this.resolveFilterLabelFromKey(key, uniqueLabels, aliases);
        if (match && !used.has(match)) {
          ordered.push(match);
          used.add(match);
        }
      });

      const remainder = uniqueLabels
        .filter(label => !used.has(label))
        .sort(typeof fallbackSort === "function" ? fallbackSort : (a, b) => naturalCompare(a, b));

      return [...ordered, ...remainder];
    }

    categoryFilterButtons(categoryKey, labels, activeValue) {
      const uniqueLabels = [...new Set(labels.filter(Boolean))];
      if (uniqueLabels.length <= 1) return "";
      const mobile = this.isMobileLayout();
      // Mobile: pills replace group headings â€” no "All", first group selected by default.
      const filterLabels = mobile ? uniqueLabels : ["All", ...uniqueLabels];
      const resolvedActive = mobile
        ? (activeValue || uniqueLabels[0] || "")
        : activeValue;
      return `
        <div class="sdkc-filter-bar">
          ${filterLabels.map(label => {
            const value = label === "All" ? "" : label;
            const active = mobile
              ? resolvedActive === label
              : ((!resolvedActive && label === "All") || resolvedActive === label);
            return `<button type="button" class="sdkc-filter-btn ${active ? "is-active" : ""}" data-category-key="${escapeHtml(categoryKey)}" data-category-filter="${escapeHtml(value)}">${escapeHtml(label)}</button>`;
          }).join("")}
        </div>
      `;
    }

    shellCardImagesMarkup(visuals, row) {
      const images = [
        { src: visuals.image, alt: visuals.altText || row.sizeLabel, label: "Front" },
        { src: visuals.image2, alt: `${visuals.altText || row.sizeLabel} back`, label: "Back" }
      ].filter(item => item.src);

      if (!images.length) return "";
      if (images.length === 1) {
        return `<img class="ddkb-shell-card-image" src="${escapeHtml(images[0].src)}" alt="${escapeHtml(images[0].alt)}" loading="lazy">`;
      }

      return `
        <div class="ddkb-shell-card-images" aria-label="${escapeHtml(row.displayLabel || row.sizeLabel)} front and back">
          ${images.map((item, index) => `
            <figure class="ddkb-shell-card-side">
              <img class="ddkb-shell-card-image" src="${escapeHtml(item.src)}" alt="${escapeHtml(item.alt)}" loading="lazy">
              <figcaption>${escapeHtml(item.label || (index === 0 ? "Front" : "Back"))}</figcaption>
            </figure>
          `).join("")}
        </div>
      `;
    }

    shellCardMarkup(row, category) {
      const selected = this.getCategoryCatalogSelection(category.key).find(item => item.catalogId === row.catalogId);
      const visuals = this.getShellImageForRow(row, category);
      const groupLabel = category.eyebrowField
        ? String(row[category.eyebrowField] || "")
        : this.shellGroupLabel(row, category.groupField || "drumTypeLabel");
      const title = category.titleField ? String(row[category.titleField] || "") : "";
      const imageMarkup = visuals?.image
        ? `<img class="ddkb-shell-card-image" src="${escapeHtml(visuals.image)}" alt="${escapeHtml(visuals.altText || row.sizeLabel)}" loading="lazy">`
        : "";
      return `
        <article class="sdkc-category-option ddkb-shell-card ${selected ? "is-selected" : ""}">
          ${imageMarkup}
          <div class="ddkb-shell-card-price">${itemMoneyPlus(row.price)}</div>
          <div class="ddkb-shell-card-meta">
            <span>${escapeHtml(groupLabel || category.label)}</span>
            <strong>${escapeHtml(row.displayLabel || title || row.sizeLabel)}</strong>
            ${row.descriptionOverride ? `<small>${escapeHtml(row.descriptionOverride)}</small>` : ""}
          </div>
          <div class="ddkb-shell-card-actions">
            <button type="button" class="sdkc-button ${selected ? "sdkc-button-secondary" : "sdkc-button-primary"} sdkc-button-compact" data-shell-category="${escapeHtml(category.key)}" data-shell-action="${escapeHtml(row.catalogId)}">
              ${selected ? "Remove" : "Add item"}
            </button>
            ${selected && category.allowQuantity ? this.quantityControlMarkup(category.key, row.catalogId, selected.quantity) : ""}
          </div>
        </article>
      `;
    }

    quantityControlMarkup(categoryKey, catalogId, quantity) {
      return `
        <div class="ddkb-quantity-control" aria-label="Item quantity">
          <button type="button" data-shell-category="${escapeHtml(categoryKey)}" data-shell-qty="${escapeHtml(catalogId)}" data-qty-delta="-1" aria-label="Decrease quantity">-</button>
          <span>${quantity}</span>
          <button type="button" data-shell-category="${escapeHtml(categoryKey)}" data-shell-qty="${escapeHtml(catalogId)}" data-qty-delta="1" aria-label="Increase quantity">+</button>
        </div>
      `;
    }

    selectedShellsMarkup(categoryKey, showTaxDisclosure = false) {
      const category = categoryKey ? this.getCategory(categoryKey) : null;
      const items = categoryKey
        ? this.getCategoryCatalogSelection(categoryKey)
          .filter(item => !category || this.catalogItemMatchesCategory(category, item))
          .map(item => {
            const row = this.catalogById(item.catalogId);
            return { ...item, row, unavailable: !row };
          })
        : this.selectedShellItems();
      if (!items.length) return "";
      return `
        <div class="ddkb-selected-shells">
          <div class="ddkb-selected-shells-head">Current selections</div>
          ${items.map(item => {
            const itemCategoryKey = categoryKey || item.categoryKey || "";
            const removeButton = `
              <button
                type="button"
                class="ddkb-selected-shell-remove"
                data-shell-category="${escapeHtml(itemCategoryKey)}"
                data-remove-selected-shell="${escapeHtml(item.catalogId)}"
                aria-label="Remove ${escapeHtml(item.row?.drumTypeLabel || item.drumType || "item")} ${escapeHtml(item.row?.sizeLabel || item.sizeKey || "")}"
              >&times;</button>
            `;
            if (!item.row) {
              return `
                <div class="ddkb-selected-shell-row ddkb-warning">
                  <span>Unavailable item: ${escapeHtml(item.drumType || "unknown")} ${escapeHtml(item.sizeKey || "")}</span>
                  <strong>Qty ${item.quantity}</strong>
                  ${removeButton}
                </div>
              `;
            }
            return `
              <div class="ddkb-selected-shell-row">
                <span>${escapeHtml(item.row.drumTypeLabel)} ${escapeHtml(item.row.sizeLabel)} / ${escapeHtml(item.row.finishName || titleFromKey(item.row.finishKey))}</span>
                <strong>${item.quantity} x ${showTaxDisclosure ? priceWithTaxMarkup(item.row.price) : money(item.row.price)}</strong>
                ${removeButton}
              </div>
            `;
          }).join("")}
        </div>
      `;
    }

    getAddonCategorySteps() {
      return this.getCategories().filter(category => category.dataSource === "addons");
    }

    addonCategoryFilterForStep(stepCategory) {
      if (!stepCategory) return "";
      if (stepCategory.addonCategory) return stepCategory.addonCategory;
      if (this.getAddonCategorySteps().length === 1) return "";
      return stepCategory.label || stepCategory.key || "";
    }

    addOnsForStep(stepCategory) {
      const filterLabel = this.addonCategoryFilterForStep(stepCategory);
      const filterKey = normalizeAddonCategoryKey(filterLabel);
      const addons = this.addOns();
      if (!filterKey) return addons;
      return addons.filter(addOn => this.addOnMatchesCategoryFilter(addOn, filterKey));
    }

    addOnMatchesCategoryFilter(addOn, filterKey) {
      if (!filterKey) return true;
      const categoryKey = normalizeAddonCategoryKey(addOn.category);
      const groupKey = normalizeAddonCategoryKey(addOn.group);
      return categoryKey === filterKey || groupKey === filterKey;
    }

    addOnFilterKey(addOn) {
      const group = String(addOn.group || "").trim();
      if (group) return group;
      return String(addOn.category || "").trim();
    }

    addOnsMarkup(stepCategory) {
      const addons = this.addOnsForStep(stepCategory);
      const stepFilter = this.addonCategoryFilterForStep(stepCategory);
      const mobile = this.isMobileLayout();

      if (!this.addOns().length) {
        return `<p>Add add-on product blocks to show hardware or accessories.</p>${this.nextButton()}`;
      }
      if (!addons.length) {
        const label = stepFilter || stepCategory?.label || "this step";
        return `<p>No add-on products are assigned to ${escapeHtml(label)}. Set the same Category on Add-on product blocks and on this step's Add-on category field.</p>${this.nextButton()}`;
      }

      const filterLabels = this.sortCategoryFilterLabels(stepCategory, [...new Set(addons.map(addOn => this.addOnFilterKey(addOn)).filter(Boolean))]);
      const storedFilter = this.state.activeAddonFilter;
      // Mobile matches category pills: no "All", first group selected by default.
      const activeFilter = mobile
        ? (storedFilter || filterLabels[0] || "")
        : storedFilter;
      const scopedAddons = activeFilter
        ? addons.filter(addOn => this.addOnFilterKey(addOn) === activeFilter)
        : addons;

      const filterButtonLabels = filterLabels.length > 1
        ? (mobile ? filterLabels : ["All", ...filterLabels])
        : [];
      const filterButtons = filterButtonLabels.map(label => {
        const value = label === "All" ? "" : label;
        const isActive = mobile
          ? activeFilter === label
          : ((label === "All" && !activeFilter) || label === activeFilter);
        return `<button type="button" class="sdkc-filter-btn ${isActive ? "is-active" : ""}" data-addon-filter="${escapeHtml(value)}">${escapeHtml(label)}</button>`;
      }).join("");

      const rowKey = stepFilter ? normalizeAddonCategoryKey(stepFilter) : "__addons__";
      const expanded = this.state.expandedAddonRows.has(rowKey);
      // Mobile shows the full add-on list; desktop keeps the show more / less control.
      const visibleAddons = mobile || expanded ? scopedAddons : scopedAddons.slice(0, 12);
      const moreButton = !mobile && scopedAddons.length > 12
        ? `<div class="sdkc-addon-row-actions"><button type="button" class="sdkc-button sdkc-button-secondary sdkc-addon-more" data-show-more-addons="${escapeHtml(rowKey)}">${expanded ? "Show less" : `Show more (${scopedAddons.length - 12})`}</button></div>`
        : "";

      return `
        ${filterButtons ? `<div class="sdkc-filter-bar">${filterButtons}</div>` : ""}
        <div class="sdkc-addon-rows">
          <section class="sdkc-addon-row">
            <div class="sdkc-addon-product-grid">
              ${visibleAddons.map(addOn => this.addOnCardMarkup(addOn)).join("")}
            </div>
            ${moreButton}
          </section>
        </div>
        ${this.nextButton()}
      `;
    }

    addOnCardMarkup(addOn) {
      const selected = this.state.addOns.has(addOn.id);
      const imageMarkup = addOn.image
        ? `<button type="button" class="sdkc-addon-image-btn" data-preview-addon="${escapeHtml(addOn.id)}" aria-label="Preview ${escapeHtml(addOn.name)}"><img src="${escapeHtml(addOn.image)}" alt="${escapeHtml(addOn.name)}" loading="lazy"></button>`
        : `<div class="sdkc-addon-placeholder">${escapeHtml(addOn.category)}</div>`;
      return `
        <article class="sdkc-addon ${selected ? "is-selected" : ""}">
          ${imageMarkup}
          <div class="sdkc-addon-copy">
            <h3>${escapeHtml(addOn.name)}</h3>
            <div class="sdkc-addon-price">${addOn.compareAtPrice > addOn.price ? `<s>${money(addOn.compareAtPrice)}</s>` : ""}<strong>${itemMoneyPlus(addOn.price)}</strong></div>
          </div>
          <div class="sdkc-addon-actions">
            <button type="button" class="sdkc-button sdkc-button-secondary sdkc-button-compact ${selected ? "is-added" : ""}" data-add-quote="${escapeHtml(addOn.id)}">${selected ? "Added to quote" : "Add to quote"}</button>
            <button type="button" class="sdkc-button sdkc-button-primary sdkc-button-compact" data-buy-now="${escapeHtml(addOn.id)}">Buy add-on</button>
          </div>
        </article>
      `;
    }

    previewAddOn(id) {
      const addOn = this.addOns().find(item => item.id === id);
      if (!addOn?.image) return;
      this.state.activeAddonPreview = {
        src: addOn.image,
        alt: addOn.name || "Add-on",
        label: addOn.name || "Add-on"
      };
      this.state.photoIndex = 0;
      // If the stage was collapsed on mobile, reveal it so the tap has a clear result.
      if (this.state.mobilePreviewHidden) this.state.mobilePreviewHidden = false;
      this.update();
    }

    savingsMarkup() {
      const total = this.estimatedTotal();
      const rewards = this.rewardPoints(total);
      const rewardsValue = this.rewardDollarValue(rewards);
      const shortPayment = total / AFFIRM_MONTHS_SHORT;
      const longTotal = total * (1 + AFFIRM_LONG_APR);
      const longPayment = longTotal / AFFIRM_MONTHS_LONG;

      return `
        <p class="ddkb-estimate-note">${escapeHtml(this.config.quoteDisclaimer)}</p>
        <div class="sdkc-savings">
          ${this.config.showRewards ? `
            <div>
              <span>${escapeHtml(this.config.rewardsLabel)}</span>
              <strong>${rewards.toLocaleString()} points &middot; Approx. ${money(rewardsValue)}</strong>
              <small>Coupon Towards Future Purchases</small>
              ${this.config.rewardsUrl ? `<a href="${escapeHtml(this.config.rewardsUrl)}">${escapeHtml(this.config.rewardsLinkLabel || "View rewards program")}</a>` : ""}
            </div>
          ` : ""}
          ${this.config.showFinancing ? `
            <div>
              <span>${escapeHtml(this.config.financingLabel)}</span>
              <strong>Approx. ${money(shortPayment)} / mo</strong>
              <small>${escapeHtml(this.config.financingDescription || "")}</small>
              ${this.config.financingUrl ? `<a href="${escapeHtml(this.config.financingUrl)}">${escapeHtml(this.config.financingLinkLabel || "View financing details")}</a>` : ""}
            </div>
          ` : ""}
        </div>
        ${this.config.showFinancing ? `
          <div class="sdkc-finance-card">
            <div><span>Sample plans based on an Approx. ${money(total)} build</span><strong>Estimated financing breakdown</strong></div>
            <div class="sdkc-finance-plan">
              <h3>Approx. ${money(shortPayment)} every month for 12 months</h3>
              <dl>
                <div><dt>Interest (0% APR)</dt><dd>${money(0)}</dd></div>
                <div><dt>Estimated total</dt><dd>${money(total)}</dd></div>
              </dl>
            </div>
            <div class="sdkc-finance-plan">
              <h3>Approx. ${money(longPayment)} every month for 24 months</h3>
              <dl>
                <div><dt>Interest (15% APR)</dt><dd>${money(longTotal - total)}</dd></div>
                <div><dt>Estimated total</dt><dd>${money(longTotal)}</dd></div>
              </dl>
            </div>
            <p>Taxes, shipping, approval, down payment, rates, and final eligibility can change the actual payment.</p>
          </div>
        ` : ""}
        <div class="sdkc-savings-build-total">
          <div class="sdkc-review-row sdkc-review-row-with-note">
            <span>Estimated total</span>
            <strong>
              ${money(total)}
              <small class="sdkc-tax-note">Before Applicable Taxes</small>
            </strong>
          </div>
        </div>
        ${this.nextButton()}
      `;
    }

    reviewMarkup() {
      const rows = [{ label: "Series", value: this.getSeriesLabel() }];
      this.getCategories().forEach(category => {
        if (category.dataSource === "savings" || category.dataSource === "review") return;
        if (category.dataSource === "catalog_field" || category.dataSource === "manual") {
          rows.push({ label: category.label, value: this.categorySelectionSummary(category.key) });
        }
        if (category.dataSource === "catalog_select") {
          rows.push({
            label: category.label,
            value: `${this.selectedShellQuantity(category.key)} shells / ${money(this.selectedShellTotal(category.key))}`,
            taxNote: true,
            selectionCategoryKey: category.key
          });
        }
      });
      rows.push({
        label: "Add-ons",
        value: this.selectedAddOns().length ? `${this.selectedAddOns().length} selected / ${money(this.addOnTotal())}` : "None",
        taxNote: this.selectedAddOns().length > 0,
        selectedAddOns: true
      });
      if (this.config.showRewards) {
        const rewards = this.rewardPoints(this.estimatedTotal());
        rows.push({
          label: this.config.rewardsLabel || "Rewards",
          value: `${rewards.toLocaleString()} points / Approx. ${money(this.rewardDollarValue(rewards))}`,
          note: "Coupon Towards Future Purchases"
        });
      }
      rows.push({ label: "Estimated total", value: money(this.estimatedTotal()), taxNote: true });
      rows.push({
        label: "Minimum deposit",
        value: `25% / ${money(this.estimatedTotal() * 0.25)}`,
        note: "Deposits are made in-store or via phone"
      });

      return `
        <div class="sdkc-review">${rows.map(row => `
          <div class="sdkc-review-row ${row.note || row.taxNote ? "sdkc-review-row-with-note" : ""}">
            <span>${escapeHtml(row.label)}</span>
            <strong>
              ${escapeHtml(row.value)}
              ${row.taxNote ? `<small class="sdkc-tax-note">before taxes</small>` : ""}
              ${row.note ? `<small>${escapeHtml(row.note)}</small>` : ""}
            </strong>
          </div>
          ${row.selectionCategoryKey ? `<div class="sdkc-review-selections">${this.selectedShellsMarkup(row.selectionCategoryKey, true)}</div>` : ""}
          ${row.selectedAddOns ? `<div class="sdkc-review-selections">${this.selectedAddOnsMarkup()}</div>` : ""}
        `).join("")}</div>
        <p class="ddkb-estimate-note">${escapeHtml(this.config.quoteDisclaimer)}</p>
        <button type="button" class="sdkc-button sdkc-button-primary sdkc-review-submit" data-request-quote>Submit this build request</button>
      `;
    }

    categorySelectionSummary(categoryKey) {
      const category = this.getCategory(categoryKey);
      if (!category) return "None";
      const value = this.getCategorySelection(categoryKey);
      if (!value || (Array.isArray(value) && !value.length)) return "None";
      const options = this.getOptionsForCategory(categoryKey);
      const labels = (Array.isArray(value) ? value : [value]).map(key => {
        const option = options.find(item => item.key === key);
        return option?.label || option?.name || titleFromKey(key);
      });
      return labels.join(", ");
    }

    selectedAddOnsMarkup() {
      const addOns = this.selectedAddOns();
      if (!addOns.length) return "";
      return `
        <div class="ddkb-selected-shells">
          <div class="ddkb-selected-shells-head">Current selections</div>
          ${addOns.map(addOn => `
            <div class="ddkb-selected-shell-row">
              <span>${escapeHtml(addOn.name)}</span>
              <strong>${priceWithTaxMarkup(addOn.price)}</strong>
              <button type="button" class="ddkb-selected-shell-remove" data-add-quote="${escapeHtml(addOn.id)}" aria-label="Remove ${escapeHtml(addOn.name)}">&times;</button>
            </div>
          `).join("")}
        </div>
      `;
    }

    loadingOrErrorMarkup(category) {
      if (category && ["addons", "savings", "review", "manual"].includes(category.dataSource)) return "";
      if (this.catalogError) return `<div class="ddkb-warning">${escapeHtml(this.catalogErrorMessage || "Builder catalog could not be loaded. Please check the catalog JSON setting.")}</div>`;
      if (!this.catalogLoaded) return `<p class="ddkb-estimate-note">Loading builder catalog...</p>`;
      return "";
    }

    nextButton() {
      return `<button type="button" class="sdkc-button sdkc-button-primary sdkc-next" data-next-step>Continue</button>`;
    }

    photosForOption(option, category) {
      if (!option) return [];
      const photos = [];
      // Stage preview uses dedicated preview images only â€” never option card thumbnails/swatches.
      const front = option.previewImage || option.frontImage || "";
      const back = option.previewImage2 || option.backImage || "";
      const label = option.label || option.name || category?.label || "Preview";
      if (front) photos.push({ src: front, alt: label, label });
      if (back) photos.push({ src: back, alt: `${label} alternate view`, label: "Alternate view" });
      return photos;
    }

    photosForCategorySelection(category) {
      if (!category) return [];
      const value = this.getCategorySelection(category.key);
      const selectedKey = Array.isArray(value) ? value[value.length - 1] : value;
      if (!selectedKey) return [];
      const option = this.getOptionsForCategory(category.key).find(item => item.key === selectedKey);
      return this.photosForOption(option, category);
    }

    photosForCatalogSelection(category) {
      if (!category || category.dataSource !== "catalog_select") return [];
      if (this.isSnarePreviewCategory(category)) return [];
      const selectedItems = this.getCategoryCatalogSelection(category.key);
      const selectedItem = selectedItems[selectedItems.length - 1];
      const row = selectedItem ? this.catalogById(selectedItem.catalogId) : null;
      if (!row) return [];

      const legacy = this.config.shellImageBlocks.byCatalogId.get(row.catalogId) ||
        (row.imageKey ? this.config.shellImageBlocks.byImageKey.get(row.imageKey) : null);
      const exactOption = this.getOptionOverride(row.catalogId, ["catalog", "any"]) ||
        (row.imageKey ? this.getOptionOverride(row.imageKey, ["image", "any"]) : null);
      const drumTypeOption = this.getOptionOverride(row.drumType, ["category", "any"]);
      const fieldOption = this.config.optionBlocks.list.find(candidate => {
        if (!this.optionTargetsCategory(candidate, category.key)) return false;
        if (candidate.matchField) return String(row[candidate.matchField] || "") === candidate.key;
        return Object.values(row).some(value =>
          (typeof value === "string" || typeof value === "number") && String(value) === candidate.key
        );
      });
      const option = exactOption || fieldOption || drumTypeOption;

      // Option blocks with only a thumbnail must not replace the stage image.
      if (option) return this.photosForOption(option, category);

      // Catalog/legacy product images are full previews (not option-card thumbs).
      const photos = [];
      if (legacy?.image) {
        photos.push({
          src: legacy.image,
          alt: legacy.altText || row.sizeLabel,
          label: legacy.altText || row.sizeLabel
        });
      }
      if (legacy?.image2) {
        photos.push({
          src: legacy.image2,
          alt: `${legacy.altText || row.sizeLabel} alternate view`,
          label: "Alternate view"
        });
      }
      return photos;
    }

    defaultPreviewOptionKey(categoryKey) {
      const category = this.getCategory(categoryKey);
      if (!category || !["catalog_field", "manual"].includes(category.dataSource)) return "";
      const options = this.getOptionsForCategory(categoryKey);
      const configuredDefault = options.find(option => option.defaultOption);
      if (configuredDefault) return configuredDefault.key;
      const field = this.catalogFieldForCategory(category);
      if (field === this.config.primaryMaterialKey || field === "wood") {
        return options.find(option => option.key === this.config.defaultWoodKey)?.key || options[0]?.key || "";
      }
      return "";
    }

    defaultPreviewPhotos() {
      const defaultKeys = new Map(this.getCategories().map(category => [category.key, this.defaultPreviewOptionKey(category.key)]));
      const combo = (this.config.imageCombos || []).find(candidate =>
        (!candidate.categoryKey1 || defaultKeys.get(candidate.categoryKey1) === candidate.optionKey1) &&
        (!candidate.categoryKey2 || defaultKeys.get(candidate.categoryKey2) === candidate.optionKey2)
      );
      const comboPhotos = this.photosFromImageCombo(combo);
      if (comboPhotos.length) return comboPhotos;

      for (const category of [...this.getCategories()].reverse()) {
        const key = defaultKeys.get(category.key);
        if (!key) continue;
        const option = this.getOptionsForCategory(category.key).find(candidate => candidate.key === key);
        const photos = this.photosForOption(option, category);
        if (photos.length) return photos;
      }
      return [];
    }

    photos() {
      const activeCategory = this.getCategory(this.state.activeStep);
      const activeIsOptionStep = activeCategory &&
        (activeCategory.dataSource === "catalog_field" || activeCategory.dataSource === "manual");

      // Tapped add-on product image â€” show in the stage viewport.
      if (activeCategory?.dataSource === "addons" && this.state.activeAddonPreview?.src) {
        return [this.state.activeAddonPreview];
      }

      const activeCatalogPhotos = this.photosForCatalogSelection(activeCategory);
      if (activeCatalogPhotos.length) return activeCatalogPhotos;

      // Active shell step with a selection but no stage-quality image (thumbnail-only option):
      // keep whatever is already on the stage instead of jumping to another category's photo.
      if (activeCategory?.dataSource === "catalog_select" &&
        this.getCategoryCatalogSelection(activeCategory.key).length) {
        if (this.state.lastDisplayedPhoto?.src) return [this.state.lastDisplayedPhoto];
      }

      if (activeIsOptionStep) {
        const activePhotos = this.photosForCategorySelection(activeCategory);
        if (activePhotos.length) return activePhotos;
        if (this.getCategorySelection(activeCategory.key)) {
          if (this.state.lastDisplayedPhoto?.src) return [this.state.lastDisplayedPhoto];
          return [{ src: this.config.heroImage, alt: this.config.heroImageAlt, label: "Hero image" }];
        }
      }

      const comboPhotos = this.photosFromImageCombo(this.getActiveImageCombo());
      if (comboPhotos.length) return comboPhotos;

      const previewCategories = [...this.getCategories()].reverse().filter(category =>
        category.dataSource === "catalog_field" || category.dataSource === "manual"
      );

      for (const category of previewCategories) {
        const categoryPhotos = this.photosForCategorySelection(category);
        if (categoryPhotos.length) return categoryPhotos;
      }

      const defaultPhotos = this.defaultPreviewPhotos();
      if (defaultPhotos.length) return defaultPhotos;

      if (this.state.lastDisplayedPhoto?.src) return [this.state.lastDisplayedPhoto];

      return [{ src: this.config.heroImage, alt: this.config.heroImageAlt, label: "Hero image" }];
    }

    syncPhotoIndexToAvailableViews() {
      const photos = this.photos();
      if (!photos.length) {
        this.state.photoIndex = 0;
        return;
      }
      if (this.state.photoIndex < 0) this.state.photoIndex = 0;
      if (this.state.photoIndex >= photos.length) {
        this.state.photoIndex = Math.max(0, photos.length - 1);
      }
    }

    rewardPoints(total) {
      return Math.round((Number(total) || 0) * REWARD_POINTS_EARNED_PER_DOLLAR);
    }

    rewardDollarValue(points) {
      return Math.round((Number(points) || 0) / REWARD_POINTS_PER_REDEMPTION_DOLLAR);
    }

    drumLineupType(row) {
      const drumType = String(row?.drumType || "").toLowerCase();
      const drumLabel = String(row?.drumTypeLabel || "").toLowerCase();
      if (drumType === "bd" || drumLabel.includes("bass")) return { key: "bass", label: "Bass drum" };
      if (drumType === "ft" || drumLabel.includes("floor")) return { key: "floor", label: "Floor tom" };
      if (drumType === "sn" || drumLabel.includes("snare")) return { key: "snare", label: "Snare" };
      return { key: "rack", label: drumLabel.includes("tom") ? "Rack tom" : "Drum" };
    }

    drumWireframeMarkup(typeKey) {
      const wireframes = {
        bass: `
          <svg viewBox="0 0 100 100" aria-hidden="true" focusable="false">
            <circle class="sdkc-wire-fill" cx="50" cy="45" r="36"/>
            <circle class="sdkc-wire-primary" cx="50" cy="45" r="36"/>
            <circle class="sdkc-wire-secondary" cx="50" cy="45" r="29.5"/>
            <g class="sdkc-wire-tension">
              <path d="M50 9v7M50 74v7M14 45h7M79 45h7M24.5 19.5l5 5M70.5 65.5l5 5M75.5 19.5l-5 5M29.5 65.5l-5 5"/>
            </g>
            <g class="sdkc-wire-hardware">
              <circle cx="50" cy="12.5" r="2.2"/><circle cx="50" cy="77.5" r="2.2"/>
              <circle cx="17.5" cy="45" r="2.2"/><circle cx="82.5" cy="45" r="2.2"/>
              <circle cx="27" cy="22" r="2.2"/><circle cx="73" cy="68" r="2.2"/>
              <circle cx="73" cy="22" r="2.2"/><circle cx="27" cy="68" r="2.2"/>
            </g>
            <path class="sdkc-wire-primary" d="M27 75L13 88M73 75l14 13M11 88h8M81 88h8"/>
            <path class="sdkc-wire-secondary" d="M46 81v8h8v-8M43 89h14"/>
          </svg>`,
        rack: `
          <svg viewBox="0 0 100 92" aria-hidden="true" focusable="false">
            <path class="sdkc-wire-primary" d="M42 12V7h16v5M37 12h26"/>
            <path class="sdkc-wire-secondary" d="M47 7V3h6v4"/>
            <path class="sdkc-wire-fill" d="M20 25c0-6 13-11 30-11s30 5 30 11v42c0 6-13 11-30 11S20 73 20 67z"/>
            <ellipse class="sdkc-wire-primary" cx="50" cy="25" rx="30" ry="11"/>
            <path class="sdkc-wire-primary" d="M20 25v42M80 25v42"/>
            <ellipse class="sdkc-wire-primary" cx="50" cy="67" rx="30" ry="11"/>
            <ellipse class="sdkc-wire-secondary" cx="50" cy="25" rx="25" ry="7"/>
            <path class="sdkc-wire-secondary" d="M22 31c14 8 42 8 56 0M22 61c14 8 42 8 56 0"/>
            <g class="sdkc-wire-tension">
              <path d="M29 31v28M43 34v29M57 34v29M71 31v28"/>
            </g>
            <g class="sdkc-wire-hardware">
              <circle cx="29" cy="31" r="2"/><circle cx="29" cy="59" r="2"/>
              <circle cx="43" cy="34" r="2"/><circle cx="43" cy="63" r="2"/>
              <circle cx="57" cy="34" r="2"/><circle cx="57" cy="63" r="2"/>
              <circle cx="71" cy="31" r="2"/><circle cx="71" cy="59" r="2"/>
            </g>
          </svg>`,
        floor: `
          <svg viewBox="0 0 100 108" aria-hidden="true" focusable="false">
            <path class="sdkc-wire-fill" d="M17 22c0-7 15-12 33-12s33 5 33 12v52c0 7-15 12-33 12S17 81 17 74z"/>
            <ellipse class="sdkc-wire-primary" cx="50" cy="22" rx="33" ry="12"/>
            <path class="sdkc-wire-primary" d="M17 22v52M83 22v52"/>
            <ellipse class="sdkc-wire-primary" cx="50" cy="74" rx="33" ry="12"/>
            <ellipse class="sdkc-wire-secondary" cx="50" cy="22" rx="27" ry="8"/>
            <path class="sdkc-wire-secondary" d="M19 29c15 9 47 9 62 0M19 68c15 9 47 9 62 0"/>
            <g class="sdkc-wire-tension">
              <path d="M27 30v36M42 33v38M58 33v38M73 30v36"/>
            </g>
            <g class="sdkc-wire-hardware">
              <circle cx="27" cy="30" r="2"/><circle cx="27" cy="66" r="2"/>
              <circle cx="42" cy="33" r="2"/><circle cx="42" cy="71" r="2"/>
              <circle cx="58" cy="33" r="2"/><circle cx="58" cy="71" r="2"/>
              <circle cx="73" cy="30" r="2"/><circle cx="73" cy="66" r="2"/>
            </g>
            <path class="sdkc-wire-primary" d="M25 76L14 99M75 76l11 23M14 99h8M78 99h8"/>
            <path class="sdkc-wire-secondary" d="M50 84v17M46 101h8"/>
          </svg>`,
        snare: `
          <svg viewBox="0 0 110 76" aria-hidden="true" focusable="false">
            <path class="sdkc-wire-fill" d="M14 23c0-7 18-12 41-12s41 5 41 12v29c0 7-18 12-41 12S14 59 14 52z"/>
            <ellipse class="sdkc-wire-primary" cx="55" cy="23" rx="41" ry="12"/>
            <path class="sdkc-wire-primary" d="M14 23v29M96 23v29"/>
            <ellipse class="sdkc-wire-primary" cx="55" cy="52" rx="41" ry="12"/>
            <ellipse class="sdkc-wire-secondary" cx="55" cy="23" rx="35" ry="8"/>
            <path class="sdkc-wire-secondary" d="M16 29c19 9 59 9 78 0M16 46c19 9 59 9 78 0"/>
            <g class="sdkc-wire-tension">
              <path d="M25 30v14M40 33v16M55 34v17M70 33v16M85 30v14"/>
            </g>
            <g class="sdkc-wire-hardware">
              <circle cx="25" cy="30" r="2"/><circle cx="25" cy="44" r="2"/>
              <circle cx="40" cy="33" r="2"/><circle cx="40" cy="49" r="2"/>
              <circle cx="55" cy="34" r="2"/><circle cx="55" cy="51" r="2"/>
              <circle cx="70" cy="33" r="2"/><circle cx="70" cy="49" r="2"/>
              <circle cx="85" cy="30" r="2"/><circle cx="85" cy="44" r="2"/>
            </g>
            <path class="sdkc-wire-tension" d="M29 54l8 5 8-3 8 5 8-5 8 3 8-5"/>
            <path class="sdkc-wire-primary" d="M96 31h7v18h-7M103 35l5-5M108 30v10"/>
          </svg>`
      };
      return wireframes[typeKey] || wireframes.rack;
    }

    drumLineupScale(typeKey, sizeLabel) {
      const dimensions = String(sizeLabel || "").match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/i);
      if (!dimensions) return { x: 1, y: 1 };

      // Catalog labels are depth Ã— diameter (e.g. 7x8 tom, 14x22 bass, 6.5x14 snare).
      const depth = Number(dimensions[1]);
      const diameter = Number(dimensions[2]);
      const clampScale = value => Math.min(1.12, Math.max(0.88, value));

      // Bass icon is face-on (round head), so overall size follows diameter only â€” the
      // number on the right. Depth must not drive icon scale or 14x22 vs 18x22 drift.
      if (typeKey === "bass") {
        const size = clampScale(1 + ((diameter - 20) * 0.02));
        return { x: size, y: size };
      }

      // Side-profile shells: width tracks diameter, height tracks depth.
      const scaleProfiles = {
        rack: { depthReference: 8, depthStep: 0.035, diameterReference: 10, diameterStep: 0.018 },
        floor: { depthReference: 14, depthStep: 0.025, diameterReference: 16, diameterStep: 0.015 },
        snare: { depthReference: 6.5, depthStep: 0.045, diameterReference: 14, diameterStep: 0.014 }
      };
      const profile = scaleProfiles[typeKey] || scaleProfiles.rack;

      return {
        x: clampScale(1 + ((diameter - profile.diameterReference) * profile.diameterStep)),
        y: clampScale(1 + ((depth - profile.depthReference) * profile.depthStep))
      };
    }

    drumLineupMarkup() {
      const typeOrder = { bass: 0, rack: 1, floor: 2, snare: 3 };
      const drums = this.selectedShellItems()
        .filter(item => item.row && item.quantity > 0)
        .flatMap(item => {
          const type = this.drumLineupType(item.row);
          const size = item.row.sizeLabel || item.sizeKey || "";
          const scale = this.drumLineupScale(type.key, size);
          return Array.from({ length: item.quantity }, (_, quantityIndex) => ({
            type,
            size,
            scale,
            catalogId: item.catalogId,
            quantityIndex
          }));
        })
        .sort((a, b) => typeOrder[a.type.key] - typeOrder[b.type.key] || a.size.localeCompare(b.size, undefined, { numeric: true }));

      if (!drums.length) {
        return `<p class="sdkc-drum-lineup-empty"><strong>Your drum lineup</strong><span>Selected drums appear here.</span></p>`;
      }

      return `
        <div class="sdkc-drum-lineup-track" role="list" aria-label="${drums.length} selected ${drums.length === 1 ? "drum" : "drums"}">
          ${drums.map(drum => `
            <div class="sdkc-drum-token sdkc-drum-${drum.type.key}" role="listitem" aria-label="${escapeHtml(`${drum.type.label}, ${drum.size}`)}" data-catalog-id="${escapeHtml(drum.catalogId)}" data-quantity-index="${drum.quantityIndex}">
              <div class="sdkc-drum-wire" style="--sdkc-drum-scale-x:${drum.scale.x.toFixed(3)};--sdkc-drum-scale-y:${drum.scale.y.toFixed(3)}">${this.drumWireframeMarkup(drum.type.key)}</div>
              <small>${escapeHtml(drum.type.label)}</small>
              <em>${escapeHtml(drum.size)}</em>
            </div>
          `).join("")}
        </div>
      `;
    }

    updateSummary() {
      const total = this.estimatedTotal();
      const rewards = this.rewardPoints(total);
      const rewardsValue = this.rewardDollarValue(rewards);
      const monthly = total / AFFIRM_MONTHS_SHORT;
      const activeCombo = this.getActiveImageCombo();
      const previewCategory = [...this.getCategories()].reverse().find(category => {
        const value = this.getCategorySelection(category.key);
        return (category.dataSource === "catalog_field" || category.dataSource === "manual") && value;
      });
      const previewValue = previewCategory ? this.getCategorySelection(previewCategory.key) : null;
      const previewKey = Array.isArray(previewValue) ? previewValue[previewValue.length - 1] : previewValue;
      const previewOption = previewCategory
        ? this.getOptionsForCategory(previewCategory.key).find(option => option.key === previewKey)
        : null;
      const photos = this.photos();
      const photo = photos[this.state.photoIndex] || photos[0];

      this.elements.total.forEach(element => element.textContent = money(total));
      this.elements.stageFinish.textContent = activeCombo?.label || previewOption?.label || previewOption?.name || "Configure your build";
      this.elements.stageFinishCategory.textContent = activeCombo ? "Combination preview" : (previewCategory?.label || "Preview");
      const hasSelectedDrums = this.selectedShellQuantity() > 0;
      this.elements.drumLineups?.forEach(lineup => {
        lineup.innerHTML = this.drumLineupMarkup();
        lineup.classList.toggle("is-empty", !hasSelectedDrums);
      });
      if (photo?.src && this.elements.photo) {
        // Only swap the image when the URL changes â€” avoids reload flash / jump on qty updates.
        const currentSrc = this.elements.photo.getAttribute("src") || "";
        if (currentSrc !== photo.src) {
          this.elements.photo.src = photo.src;
        }
        if ((this.elements.photo.alt || "") !== (photo.alt || "")) {
          this.elements.photo.alt = photo.alt || "";
        }
        this.elements.photo.style.display = "";
        this.state.lastDisplayedPhoto = {
          src: photo.src,
          alt: photo.alt || "",
          label: photo.label || "Preview"
        };
      } else if (this.state.lastDisplayedPhoto?.src && this.elements.photo) {
        // Thumbnail-only selections: keep the last real stage image on screen.
        const keep = this.state.lastDisplayedPhoto;
        const currentSrc = this.elements.photo.getAttribute("src") || "";
        if (currentSrc !== keep.src) this.elements.photo.src = keep.src;
        if ((this.elements.photo.alt || "") !== (keep.alt || "")) this.elements.photo.alt = keep.alt || "";
        this.elements.photo.style.display = "";
      }
      this.elements.galleryPrev.hidden = this.state.photoIndex <= 0 || photos.length <= 1;
      this.elements.galleryNext.hidden = this.state.photoIndex >= photos.length - 1 || photos.length <= 1;
      this.updatePhotoViewer(photos);
      this.elements.detailKicker.textContent = "Configure";
      const activeCategory = this.getCategory(this.state.activeStep);
      this.elements.detailTitle.textContent = activeCategory?.label || "Configure";
      if (this.elements.shellSizeFormat) {
        this.elements.shellSizeFormat.hidden = activeCategory?.key !== "shells";
      }
      const stepOrder = this.getStepOrder();
      const activeStepIndex = stepOrder.indexOf(this.state.activeStep);
      this.elements.progressCopy.textContent = stepOrder.length
        ? `Step ${activeCategory?.stepNumber ?? activeStepIndex + 1} of ${stepOrder.length}`
        : "";
      const railToggle = this.root.querySelector("[data-toggle-rail]");
      if (railToggle) {
        railToggle.textContent = this.state.railCollapsed ? "Show steps" : "Hide steps";
        railToggle.setAttribute("aria-expanded", this.state.railCollapsed ? "false" : "true");
      }
      this.syncMobilePreviewVisibility();
      if (this.elements.quoteEyebrow) {
        this.elements.quoteEyebrow.textContent = this.config.showRewards
          ? `Approx. ${money(rewardsValue)} Coupon Towards Future Purchases`
          : "";
      }
      if (this.elements.quoteSummary) {
        this.elements.quoteSummary.textContent = this.buildQuoteSummary();
      }
      if (this.elements.financeSummary) {
        this.elements.financeSummary.textContent = this.config.showFinancing && total > 0
          ? `Approx. ${money(monthly)} / mo`
          : "";
      }
      if (this.elements.output) this.elements.output.value = this.buildSummaryText();
    }

    updateUrl() {
      const url = new URL(window.location.href);
      url.searchParams.delete("series");
      if (this.state.activeStep) url.searchParams.set("step", this.state.activeStep);
      else url.searchParams.delete("step");
      url.searchParams.delete("view");
      url.searchParams.set("kitView", this.state.photoIndex === 1 ? "back" : "front");
      url.searchParams.delete("shellGroup");

      this.getCategories().forEach(category => {
        if (category.dataSource === "catalog_field" || category.dataSource === "manual") {
          const value = this.getCategorySelection(category.key);
          if (value == null || value === "") url.searchParams.delete(category.key);
          else url.searchParams.set(category.key, Array.isArray(value) ? value.join(",") : value);

          const legacyParam = this.legacyParamForCategory(category);
          if (legacyParam && legacyParam !== category.key) {
            if (value == null || value === "") url.searchParams.delete(legacyParam);
            else url.searchParams.set(legacyParam, Array.isArray(value) ? value[0] : value);
          }
          return;
        }

        if (category.dataSource === "catalog_select") {
          const shellParam = this.getCategoryCatalogSelection(category.key).map(item => {
            const row = this.catalogById(item.catalogId);
            const exactCatalogId = row?.catalogId || item.catalogId;
            if (exactCatalogId) return `${exactCatalogId}@${item.quantity}`;
            const drumType = row?.drumType || item.drumType;
            const sizeKey = row?.sizeKey || item.sizeKey;
            if (!drumType || !sizeKey) return "";
            return `${drumType}:${sizeKey}x${item.quantity}`;
          }).filter(Boolean).join(",");
          if (shellParam) {
            url.searchParams.set(category.key, shellParam);
            // Only the main shells step aliases to legacy shells= â€” never clear it when writing snare.
            if (category.key === "shells") url.searchParams.set("shells", shellParam);
          } else {
            url.searchParams.delete(category.key);
            if (category.key === "shells") url.searchParams.delete("shells");
          }
        }
      });

      Object.entries(this.state.categoryFilters || {}).forEach(([categoryKey, value]) => {
        const category = this.getCategory(categoryKey);
        if (!category) return;
        const filterParam = this.categoryFilterParamName(category);
        if (!filterParam) return;
        if (!value) {
          url.searchParams.delete(filterParam);
          return;
        }
        url.searchParams.set(filterParam, value);
      });

      if (this.state.addOns.size) url.searchParams.set("addons", [...this.state.addOns].join(","));
      else url.searchParams.delete("addons");
      window.history.replaceState({}, "", url);
    }

    stepSummary(stepKey) {
      const category = this.getCategory(stepKey);
      if (!category) return "";
      if (category.dataSource === "catalog_field" || category.dataSource === "manual") {
        return this.categorySelectionSummary(stepKey);
      }
      if (category.dataSource === "catalog_select") {
        return `${this.selectedShellQuantity(stepKey)} shells / ${money(this.selectedShellTotal(stepKey))}`;
      }
      if (category.dataSource === "addons") {
        const stepAddons = this.addOnsForStep(category);
        const stepIds = new Set(stepAddons.map(item => item.id));
        const count = [...this.state.addOns].filter(id => stepIds.has(id)).length;
        return `${count} selected`;
      }
      if (category.dataSource === "savings") {
        const total = this.estimatedTotal();
        const summaryParts = [];
        if (this.config.showRewards) {
          const rewards = this.rewardPoints(total);
          const rewardsValue = this.rewardDollarValue(rewards);
          summaryParts.push(`${rewards.toLocaleString()} points / Approx. ${money(rewardsValue)} Coupon Towards Future Purchases`);
        }
        return summaryParts.join(" / ");
      }
      if (category.dataSource === "review") return money(this.estimatedTotal());
      return "";
    }

    buildQuoteSummary() {
      const parts = this.getCategories()
        .filter(category => category.dataSource === "catalog_field" || category.dataSource === "manual")
        .map(category => this.categorySelectionSummary(category.key))
        .filter(value => value && value !== "None");
      const shellQuantity = this.getCatalogSelectCategories()
        .filter(category => !this.isSnareCategory(category))
        .reduce((sum, category) => sum + this.selectedShellQuantity(category.key), 0);
      const snareQuantity = this.getCatalogSelectCategories()
        .filter(category => this.isSnareCategory(category))
        .reduce((sum, category) => sum + this.selectedShellQuantity(category.key), 0);
      if (shellQuantity) parts.push(`${shellQuantity} ${shellQuantity === 1 ? "shell" : "shells"}`);
      if (snareQuantity) parts.push(`${snareQuantity} ${snareQuantity === 1 ? "snare" : "snares"}`);
      const addOnQuantity = this.selectedAddOns().length;
      if (addOnQuantity) parts.push(`${addOnQuantity} ${addOnQuantity === 1 ? "add-on" : "add-ons"}`);
      return parts.length ? parts.join(" / ") : "Start configuring";
    }

    isSnareCategory(category) {
      if (!category) return false;
      const identifiers = [category.key, category.label, ...category.includeValues]
        .map(normalizeAddonCategoryKey);
      return identifiers.some(value => value === "sn" || value.includes("snare"));
    }

    isSnarePreviewCategory(category) {
      if (!category) return false;
      const key = normalizeAddonCategoryKey(category.key);
      if (key === "snare" || key === "sn") return true;
      const included = (category.includeValues || []).map(normalizeAddonCategoryKey);
      const excluded = (category.excludeValues || []).map(normalizeAddonCategoryKey);
      return included.some(value => value === "sn" || value.includes("snare")) &&
        !excluded.some(value => value === "sn" || value.includes("snare"));
    }

    categoryFilterParamName(category) {
      if (!category) return "";
      return `${category.key}Group`;
    }

    selectedShellQuantity(categoryKey) {
      const items = categoryKey
        ? this.getCategoryCatalogSelection(categoryKey)
        : this.selectedShellItems();
      return items.reduce((sum, item) => sum + item.quantity, 0);
    }

    getSeriesLabel() {
      const row = this.catalog.find(item => item.series === this.state.series);
      return row?.seriesLabel || titleFromKey(this.state.series);
    }

    addOns() {
      return this.config.addOns || [];
    }

    selectedAddOns() {
      return this.addOns().filter(item => this.state.addOns.has(item.id));
    }

    shareBuild() {
      const url = window.location.href;
      if (navigator.share) {
        navigator.share({ title: this.config.productTitle, url }).catch(() => {});
        return;
      }
      navigator.clipboard?.writeText(url);
    }

    openQuoteModal() {
      // After submit, keep the confirmation open until the customer closes it.
      if (this.state.quoteSubmitted && this.elements.submitConfirmation) {
        this.showQuoteConfirmation();
        return;
      }

      this.populateQuoteFormFields();
      this.prepareContactFormSubmit();
      if (this.elements.output) this.elements.output.value = this.buildSummaryText();
      this.elements.quoteForm.hidden = false;
      if (this.elements.quoteRequestIntro) this.elements.quoteRequestIntro.hidden = false;
      this.elements.submitConfirmation.hidden = true;
      this.elements.modal?.classList.remove("is-confirmation");
      this.setQuoteFormStatus("");
      const sendButton = this.elements.quoteForm.querySelector("[data-send-quote]");
      if (sendButton) {
        sendButton.disabled = false;
        sendButton.textContent = "Send request";
      }
      this.elements.modal.showModal();
    }

    contactFormAction() {
      const root = window.Shopify?.routes?.root || "/";
      const base = window.location.origin + (root.endsWith("/") ? root : `${root}/`);
      // No hash on POST action â€” fragments are not sent and can confuse some stacks.
      return new URL("contact", base).toString();
    }

    prepareContactFormSubmit() {
      const form = this.elements.quoteForm;
      if (!form) return null;

      if (!form.getAttribute("action")) form.action = this.contactFormAction();
      if (!form.getAttribute("method")) form.method = "post";
      if (!form.getAttribute("accept-charset")) form.setAttribute("accept-charset", "UTF-8");
      form.setAttribute("data-shopify-captcha", "true");
      form.removeAttribute("target");

      if (!form.querySelector('input[name="form_type"]')) {
        const formType = document.createElement("input");
        formType.type = "hidden";
        formType.name = "form_type";
        formType.value = "contact";
        form.prepend(formType);
      }
      if (!form.querySelector('input[name="return_to"]')) {
        const returnTo = document.createElement("input");
        returnTo.type = "hidden";
        returnTo.name = "return_to";
        returnTo.setAttribute("data-return-to", "");
        form.prepend(returnTo);
        this.elements.returnTo = returnTo;
      }
      if (!form.querySelector('input[name="utf8"]')) {
        const utf8 = document.createElement("input");
        utf8.type = "hidden";
        utf8.name = "utf8";
        utf8.value = "\u2713";
        form.prepend(utf8);
      }
      return form;
    }

    submitQuoteForm(event) {
      const form = this.elements.quoteForm;
      if (
        !form ||
        this.state.quoteSubmitted ||
        this.state.quoteSending ||
        form.dataset.quoteLocked === "true" ||
        form.dataset.sending === "true"
      ) {
        event?.preventDefault();
        return;
      }
      if (!form.reportValidity()) {
        event?.preventDefault();
        return;
      }

      this.prepareContactFormSubmit();
      this.state.quoteSending = true;
      form.dataset.sending = "true";
      this.setQuoteFormStatus("");
      this.populateQuoteFormFields(true);
      this.persistQuoteConfirmationSnapshot();

      const sendButton = form.querySelector("[data-send-quote]");
      if (sendButton) {
        sendButton.disabled = true;
        sendButton.textContent = "Sending...";
      }
      // Do not disable field inputs before native submit â€” disabled controls are omitted from POST.

      if (this.shouldSimulateContactSubmit()) {
        event?.preventDefault();
        this.showQuoteConfirmation();
      }
    }

    showQuoteConfirmation() {
      this.state.quoteSubmitted = true;
      this.state.quoteSending = false;
      const form = this.elements.quoteForm;
      if (form) {
        form.hidden = true;
        form.setAttribute("data-quote-locked", "true");
        form.querySelectorAll("input, textarea, button, select").forEach(el => {
          el.disabled = true;
        });
      }
      if (this.elements.quoteRequestIntro) this.elements.quoteRequestIntro.hidden = true;
      this.elements.modal?.classList.add("is-confirmation");
      try {
        this.populateConfirmationPage();
      } catch (error) {
        this.warn("confirmation populate failed", error);
      }
      if (this.elements.submitConfirmation) {
        this.elements.submitConfirmation.hidden = false;
        this.elements.submitConfirmation.focus({ preventScroll: true });
      }
      if (this.elements.modal && !this.elements.modal.open) {
        this.elements.modal.showModal();
      }
    }

    formContactValue(fieldName) {
      const field = this.elements.quoteForm?.elements?.namedItem(fieldName);
      return String(field?.value || "").trim();
    }

    confirmationFirstName() {
      const fullName = this.formContactValue("contact[name]");
      const first = fullName.split(/\s+/).filter(Boolean)[0] || "";
      if (!first) return "there";
      // Title case so "ANTHONY" / "anthony" both become "Anthony"
      return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
    }

    confirmationBuildSummary() {
      const parts = [];
      parts.push(this.config.productTitle || "Custom drum kit");
      parts.push(this.getSeriesLabel());

      this.getCategories().forEach(category => {
        if (category.dataSource === "catalog_field" || category.dataSource === "manual") {
          const summary = this.categorySelectionSummary(category.key);
          if (summary && summary !== "None") {
            parts.push(`${category.label}: ${summary}`);
          }
        }
      });

      this.getCatalogSelectCategories().forEach(category => {
        const items = this.selectedShellItems().filter(item => item.categoryKey === category.key);
        if (!items.length) return;
        const label = this.isSnareCategory(category) ? "Snares" : category.label;
        const names = items.map(item => {
          if (!item.row) return `${item.quantity}x unavailable shell`;
          const size = item.row.sizeLabel || item.row.sizeKey || "";
          const type = item.row.drumTypeLabel || titleFromKey(item.row.drumType) || "";
          const qty = item.quantity > 1 ? `${item.quantity}x ` : "";
          return `${qty}${[type, size].filter(Boolean).join(" ")}`.trim();
        }).filter(Boolean);
        if (names.length) parts.push(`${label}: ${names.join(", ")}`);
      });

      const addOns = this.selectedAddOns();
      if (addOns.length) {
        parts.push(`Add-ons: ${addOns.map(item => item.name).join(", ")}`);
      }

      return parts.filter(Boolean).join(" / ");
    }

    populateConfirmationPage() {
      const snapshot = this.readQuoteConfirmationSnapshot() || {};
      const firstName = snapshot.firstName || this.confirmationFirstName();
      const quoteNumber = snapshot.quoteNumber || this.state.quoteNumber || "Pending";
      const price = snapshot.price || quoteMoney(this.estimatedTotal());
      const build = snapshot.build || this.confirmationBuildSummary();
      const url = snapshot.shareUrl || this.cleanShareUrl();
      const email = snapshot.supportEmail || this.config.supportEmail || "";
      const phone = snapshot.supportPhone || this.config.supportPhone || "";

      if (this.elements.confirmationGreeting) {
        this.elements.confirmationGreeting.textContent = `Hi ${firstName},`;
        this.elements.confirmationGreeting.style.textTransform = "none";
      }
      if (this.elements.confirmationMessage) {
        this.elements.confirmationMessage.textContent =
          "Your custom kit request has been submitted. The Drumland team will review your build and reach out shortly to confirm pricing, availability, and next steps.";
      }
      if (this.elements.confirmationQuote) {
        this.elements.confirmationQuote.textContent = quoteNumber;
      }
      if (this.elements.confirmationPrice) {
        this.elements.confirmationPrice.textContent = price;
      }
      if (this.elements.confirmationBuild) {
        this.elements.confirmationBuild.textContent = build;
      }
      if (this.elements.confirmationUrl) {
        this.elements.confirmationUrl.href = url;
        this.elements.confirmationUrl.textContent = url;
      }
      if (this.elements.confirmationContact) {
        const lines = ['<span class="sdkc-confirmation-contact-name">Drumland</span>'];
        if (phone) lines.push(`<span>Phone: <a href="tel:${escapeHtml(phone.replace(/[^\d+]/g, ""))}">${escapeHtml(phone)}</a></span>`);
        if (email) lines.push(`<span>Email: <a href="mailto:${escapeHtml(email)}">${escapeHtml(email)}</a></span>`);
        if (!phone && !email) {
          lines.push("<span>Our team will contact you using the details you provided.</span>");
        }
        this.elements.confirmationContact.innerHTML = lines.join("");
      }
    }

    printConfirmation({ saveAsPdf = false } = {}) {
      const sheet = this.elements.confirmationSheet;
      if (!sheet) return;

      const title = this.state.quoteNumber
        ? `Drumland Ref # ${this.state.quoteNumber}`
        : "Drumland Custom Kit Confirmation";

      const styles = `
        :root { color-scheme: light; }
        * { box-sizing: border-box; }
        body {
          margin: 0;
          padding: 32px;
          color: #111318;
          background: #fff;
          font-family: "Avenir Next", "Helvetica Neue", Helvetica, Arial, sans-serif;
          font-weight: 400;
          line-height: 1.5;
        }
        .sdkc-confirmation-eyebrow {
          margin: 0 0 6px;
          color: #007cf2;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: .1em;
          text-transform: uppercase;
        }
        h2 {
          margin: 0 0 10px;
          font-size: 26px;
          font-weight: 600;
          line-height: 1.15;
          letter-spacing: -.02em;
          text-transform: none;
        }
        .sdkc-confirmation-intro p:last-child {
          margin: 0;
          color: #4c525b;
          font-size: 15px;
          font-weight: 400;
        }
        .sdkc-confirmation-meta {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px 18px;
          margin: 24px 0 0;
          padding: 18px;
          border: 1px solid #d7dad7;
          border-radius: 12px;
          background: #fbfbfa;
        }
        .sdkc-confirmation-meta-wide { grid-column: 1 / -1; }
        dt {
          margin: 0 0 4px;
          color: #4c525b;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: .08em;
          text-transform: uppercase;
        }
        dd {
          margin: 0;
          font-size: 14px;
          font-weight: 400;
          line-height: 1.45;
          word-break: break-word;
        }
        a { color: #007cf2; word-break: break-all; font-weight: 400; }
        .sdkc-confirmation-contact {
          display: grid;
          gap: 4px;
          margin-top: 22px;
          padding-top: 16px;
          border-top: 1px solid #d7dad7;
          font-size: 14px;
          font-weight: 400;
        }
        .sdkc-confirmation-contact-name { font-weight: 600; }
        .sdkc-confirmation-footnote {
          margin: 16px 0 0;
          color: #4c525b;
          font-size: 12px;
          font-weight: 400;
        }
        .sdkc-print-hint {
          margin: 18px 0 0;
          color: #4c525b;
          font-size: 12px;
        }
        @media print {
          body { padding: 12mm; }
          .sdkc-print-hint { display: none; }
        }
      `;

      const hint = saveAsPdf
        ? `<p class="sdkc-print-hint">In the print dialog, choose <strong>Save as PDF</strong> to save your build.</p>`
        : "";

      const html = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(title)}</title>
  <style>${styles}</style>
</head>
<body>
  ${sheet.outerHTML}
  ${hint}
</body>
</html>`;

      // Hidden iframe print â€” avoids about:blank popup windows from window.open().
      const frame = document.createElement("iframe");
      frame.setAttribute("aria-hidden", "true");
      frame.title = saveAsPdf ? "Save confirmation as PDF" : "Print confirmation";
      Object.assign(frame.style, {
        position: "fixed",
        right: "0",
        bottom: "0",
        width: "0",
        height: "0",
        border: "0",
        opacity: "0",
        pointerEvents: "none"
      });
      document.body.appendChild(frame);

      const frameWindow = frame.contentWindow;
      const frameDoc = frame.contentDocument || frameWindow?.document;
      if (!frameWindow || !frameDoc) {
        frame.remove();
        window.alert("Unable to open the print dialog. Please try again.");
        return;
      }

      frameDoc.open();
      frameDoc.write(html);
      frameDoc.close();

      const cleanup = () => {
        window.setTimeout(() => {
          try { frame.remove(); } catch (_) { /* ignore */ }
        }, 1000);
      };

      const triggerPrint = () => {
        try {
          frameWindow.focus();
          frameWindow.print();
        } catch (error) {
          this.warn("confirmation print failed", error);
        }
        cleanup();
      };

      // Give the frame a moment to layout before invoking the system print dialog.
      if (frameDoc.readyState === "complete") {
        window.setTimeout(triggerPrint, 100);
      } else {
        frame.addEventListener("load", () => window.setTimeout(triggerPrint, 50), { once: true });
        window.setTimeout(triggerPrint, 400);
      }
    }

    createQuoteNumber() {
      const form = this.elements.quoteForm;
      const name = String(form?.elements?.namedItem("contact[name]")?.value || "").trim();
      const phone = String(form?.elements?.namedItem("contact[phone]")?.value || "").replace(/\D/g, "");
      const lastName = name.split(/\s+/).filter(Boolean).pop() || "GUEST";
      const nameCode = lastName.replace(/[^a-z0-9]/gi, "").slice(0, 4).toUpperCase() || "GUEST";
      const phoneCode = phone.slice(-4) || "0000";
      const dateCode = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      return `DKB-${dateCode}-${nameCode}-${phoneCode}`;
    }

    populateQuoteFormFields(createQuoteNumber = false) {
      if (createQuoteNumber) this.state.quoteNumber = this.createQuoteNumber();
      const summary = this.buildSummaryText();
      const total = quoteMoney(this.estimatedTotal());
      if (this.elements.quoteSubject) {
        this.elements.quoteSubject.value = `New Custom Kit Build Request${this.state.quoteNumber ? ` - ${this.state.quoteNumber}` : ""}`;
      }
      if (this.elements.quoteNumber) this.elements.quoteNumber.value = this.state.quoteNumber || "";
      if (this.elements.totalHidden) this.elements.totalHidden.value = total;
      // Single body field â€” do not also send Build Summary / Build JSON (duplicates the email).
      if (this.elements.bodyHidden) this.elements.bodyHidden.value = summary;
      if (this.elements.output) this.elements.output.value = summary;
      if (this.elements.returnTo) this.elements.returnTo.value = this.buildContactReturnPath();
    }

    updateStickyOffset() {
      this.root.style.setProperty("--sdkc-sticky-top", `${window.innerWidth <= 900 ? 0 : 182}px`);
      this.syncModuleHeights();
    }

    syncModuleHeights() {
      const stage = this.root.querySelector(".sdkc-stage");
      const rail = this.elements.stepRail;
      const detail = this.root.querySelector(".sdkc-detail-panel");
      if (!stage || !rail) return;

      if (window.innerWidth <= 900) {
        rail.style.minHeight = "";
        if (detail) detail.style.maxHeight = "";
        return;
      }

      const stageHeight = Math.round(stage.getBoundingClientRect().height);
      if (!stageHeight) return;
      rail.style.minHeight = `${stageHeight}px`;
      if (detail) detail.style.maxHeight = `${stageHeight}px`;
    }

    mountMotion() {
      if (!window.gsap || !window.ScrollTrigger || !this.config.enableMotion) return;
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      const gsap = window.gsap;
      gsap.registerPlugin(window.ScrollTrigger);
      gsap.from(this.root.querySelectorAll(".sdkc-detail-panel, .sdkc-step-rail"), {
        y: 18,
        duration: 0.7,
        ease: "power3.out",
        stagger: 0.05,
        scrollTrigger: {
          trigger: this.root.querySelector("[data-builder]"),
          start: "top 76%"
        }
      });
    }

    warn(message, detail) {
      const key = `${message}:${JSON.stringify(detail || "")}`;
      if (this.validationWarnings.has(key)) return;
      this.validationWarnings.add(key);
      console.warn(`[Dream Drum Kit Builder] ${message}`, detail || "");
    }
  }

  async function fetchJson(url, fallback) {
    if (!url) return fallback;
    try {
      const response = await fetch(url, { credentials: "same-origin" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    } catch (error) {
      console.warn("[Dream Drum Kit Builder] catalog file failed to load", error);
      return fallback;
    }
  }

  function normalizeAddOns(addOns) {
    const seen = new Set();
    return (Array.isArray(addOns) ? addOns : []).reduce((items, item, index) => {
      if (item.available === false) return items;
      const variantId = String(item.variantId || "");
      const id = String(item.id || variantId || slugify(item.name) || `add-on-${index}`);
      const dedupeKey = [item.sourceBlockId || "", variantId || item.productUrl || id].filter(Boolean).join("::");
      if (seen.has(dedupeKey)) return items;
      seen.add(dedupeKey);
      items.push({
        id,
        name: item.name || "Add-on",
        category: item.category || "Add-On",
        group: item.group || "",
        price: toNumber(item.price),
        compareAtPrice: toNumber(item.compareAtPrice),
        available: item.available !== false,
        sourceBlockId: item.sourceBlockId || "",
        sourceBlockLabel: item.sourceBlockLabel || "",
        productUrl: item.productUrl || "",
        variantId,
        modelNumber: String(item.modelNumber || "").trim(),
        stockNumber: String(item.stockNumber || "").trim(),
        image: item.image || "",
        description: item.description || ""
      });
      return items;
    }, []);
  }

  function safeJson(value, fallback) {
    try {
      return value ? JSON.parse(value) : fallback;
    } catch (error) {
      return fallback;
    }
  }

  function toNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) ? Math.max(0, Math.round(number)) : 0;
  }

  function optionalNumber(value) {
    if (value == null || String(value).trim() === "") return null;
    const cleanValue = String(value).replace(/[$,]/g, "").trim();
    const number = Number(cleanValue);
    return Number.isFinite(number) ? Math.max(0, Math.round(number)) : null;
  }

  function money(value) {
    return `$${Math.round(Number(value) || 0).toLocaleString()}`;
  }

  function quoteMoney(value) {
    return `${money(value)} before taxes`;
  }

  function priceWithTaxMarkup(value) {
    return `<span class="sdkc-price-with-tax"><span>${money(value)}</span><small>before taxes</small></span>`;
  }

  function itemMoneyPlus(value) {
    const amount = Math.round(Number(value) || 0);
    if (amount <= 0) return "Included";
    return `+${money(amount)}`;
  }

  function slugify(value) {
    return String(value || "").toLowerCase().replace(/[^a-z0-9.]+/g, "-").replace(/(^-|-$)/g, "");
  }

  function normalizeAddonCategoryKey(value) {
    return String(value || "").trim().toLowerCase();
  }

  function parseDelimitedList(value) {
    if (Array.isArray(value)) return value.map(item => String(item || "").trim()).filter(Boolean);
    return String(value || "").split(",").map(item => item.trim()).filter(Boolean);
  }

  function titleFromKey(value) {
    return String(value || "").replace(/[-_]/g, " ").replace(/\b\w/g, char => char.toUpperCase());
  }

  function naturalCompare(a, b) {
    return String(a || "").localeCompare(String(b || ""), undefined, { numeric: true, sensitivity: "base" });
  }

  function sortBySortOrderThenName(a, b) {
    return a.sortOrder - b.sortOrder || naturalCompare(a.name, b.name);
  }

  function sortBySortOrderThenLabel(a, b) {
    return a.sortOrder - b.sortOrder || naturalCompare(a.label, b.label);
  }

  function groupBy(items, keyFn) {
    return items.reduce((groups, item) => {
      const key = keyFn(item);
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
      return groups;
    }, {});
  }

  function escapeHtml(value) {
    const div = document.createElement("div");
    div.textContent = value == null ? "" : String(value);
    return div.innerHTML;
  }

  function init() {
    document.querySelectorAll("[data-dream-drum-kit-builder]").forEach(root => {
      if (root.dataset.initialized) return;
      root.dataset.initialized = "true";
      new DreamDrumKitBuilder(root);
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
  document.addEventListener("shopify:section:load", init);
})();
